import { business } from "./business";
import { newServiceDrafts } from "./service-expansion";

/**
 * Public service catalogue for Revolin Roofing.
 *
 * CRITICAL: Do not import docs/internal financial planning data here.
 * Target ROI and internal margin figures must never ship in the public bundle.
 */

export type ServiceImageKey =
  | "hero"
  | "shingles"
  | "metal"
  | "flat"
  | "emergency-roof-leak-repair"
  | "flashing-roof-penetration-repair"
  | "moss-removal-roof-gutter"
  | "asphalt-shingle-replacement"
  | "roof-health-check-maintenance"
  | "shingle-spot-repair"
  | "gutter-cleaning-downspout-flush"
  | "storm-tarping-emergency-dry-in"
  | "skylight-leak-flashing-repair"
  | "chimney-flashing-repair"
  | "gutter-replacement"
  | "gutter-guard-installation"
  | "roof-ventilation-upgrade"
  | "fascia-soffit-repair-replacement"
  | "cedar-shake-repair-restoration"
  | "flat-low-slope-roof-repair"
  | "metal-roof-repair-maintenance"
  | "cedar-shake-roof-replacement"
  | "metal-roof-replacement"
  | "flat-roof-replacement-sbs-tpo-pvc";

export type ServiceCategoryId =
  | "emergency-repairs"
  | "replacement"
  | "maintenance"
  | "gutters-exterior"
  | "commercial-strata"
  | "installation"
  | "roof-conversions"
  | "roof-systems"
  | "roofing-components"
  | "cedar"
  | "storm";

export type ServiceCustomerType =
  "homeowners" | "commercial" | "strata" | "multi-family" | "property-managers" | "builders";

export type ServiceHero = {
  eyebrow: string;
  title: string;
  body: string;
};

export type ServiceCta = {
  label: string;
  path: string;
};

export type ServiceConceptImage = {
  src: string;
  width: number;
  height: number;
  alt: string;
};

/** Minimum approved data shape for a service held behind the publication flag. */
export type ServiceDraft = {
  slug: string;
  title: string;
  shortTitle?: string;
  category: ServiceCategoryId;
  description: string;
  customerTypes: ServiceCustomerType[];
  roofSystems: string[];
  emergencyEligible?: boolean;
  plannedEligible?: boolean;
  provinceWideEligible?: boolean;
  relatedServiceSlugs?: string[];
  benefits?: string[];
  image?: ServiceImageKey;
  /** Approval switch. New capabilities remain unavailable until explicitly enabled. */
  enabled?: boolean;
};

export type ServicePage = {
  /** Stable catalogue identifier. Defaults to the canonical slug. */
  id: string;
  slug: string;
  /** Canonical public path, always under /services/ */
  path: string;
  title: string;
  shortTitle: string;
  /** Explicit naming aliases used by catalogue, navigation and integrations. */
  name: string;
  shortName: string;
  eyebrow: string;
  category: ServiceCategoryId;
  description: string;
  /** Concise AEO-friendly definition */
  definition: string;
  metaTitle: string;
  metaDescription: string;
  image: ServiceImageKey;
  imageAlt: string;
  /** Unique aspirational visual prepared for unpublished expansion services. */
  conceptImage?: ServiceConceptImage;
  hero: ServiceHero;
  benefits: string[];
  cta: ServiceCta;
  existingService: boolean;
  newService: boolean;
  emergencyEligible: boolean;
  plannedEligible: boolean;
  provinceWideEligible: boolean;
  customerTypes: ServiceCustomerType[];
  roofSystems: string[];
  featured: boolean;
  enabled: boolean;
  /** Naming alias retained for external consumers; canonical content uses relatedServiceSlugs. */
  relatedServices: string[];
  forWhom: string;
  warningSigns: string[];
  evaluates: string[];
  causes: string[];
  methodology: string[];
  materials: string[];
  bcClimate: string;
  whenAppropriate: string;
  whenOtherService: string;
  process: string[];
  relatedServiceSlugs: string[];
  relatedAreaSlugs: string[];
  solutionCategories: { title: string; body: string }[];
  repairVsReplace: { repair: string; replace: string };
  scopeFactors: string[];
  faqs: { question: string; answer: string }[];
  /**
   * Approximate public range language. Only rendered when
   * business.showPublicPricing === true.
   */
  publicPriceHint: string;
};

export const SERVICE_CATEGORY_ORDER: readonly ServiceCategoryId[] = [
  "emergency-repairs",
  "replacement",
  "maintenance",
  "gutters-exterior",
  "commercial-strata",
  "installation",
  "roof-conversions",
  "roof-systems",
  "roofing-components",
  "cedar",
  "storm",
] as const;

export const SERVICE_CATEGORY_LABELS: Record<ServiceCategoryId, string> = {
  "emergency-repairs": "Emergency + Repairs",
  replacement: "Roof Replacement",
  maintenance: "Maintenance",
  "gutters-exterior": "Gutters + Exterior Roof Components",
  "commercial-strata": "Commercial + Strata",
  installation: "Roof Installation",
  "roof-conversions": "Roof Conversions",
  "roof-systems": "Additional Roof Systems",
  "roofing-components": "Roofing Components",
  cedar: "Cedar Roofing",
  storm: "Storm Roofing",
};

export const FEATURED_SERVICE_SLUGS: readonly string[] = [
  "asphalt-shingle-replacement",
  "emergency-roof-leak-repair",
  "metal-roof-replacement",
  "flat-roof-replacement-sbs-tpo-pvc",
  "roof-health-check-maintenance",
  "moss-removal-roof-gutter",
];

type ExpansionMetadataKey =
  | "id"
  | "name"
  | "shortName"
  | "imageAlt"
  | "hero"
  | "benefits"
  | "cta"
  | "existingService"
  | "newService"
  | "emergencyEligible"
  | "plannedEligible"
  | "provinceWideEligible"
  | "customerTypes"
  | "roofSystems"
  | "featured"
  | "enabled"
  | "relatedServices";

type ExistingServicePageInput = Omit<ServicePage, ExpansionMetadataKey>;

function inferRoofSystems(serviceName: string): string[] {
  const value = serviceName.toLowerCase();
  if (value.includes("flat") || value.includes("low-slope")) {
    return ["SBS", "TPO", "PVC", "low-slope membranes"];
  }
  if (value.includes("metal")) return ["metal"];
  if (value.includes("cedar")) return ["cedar shakes", "cedar shingles"];
  if (value.includes("shingle")) return ["asphalt shingles"];
  if (value.includes("gutter") || value.includes("downspout")) {
    return ["gutters and roof drainage"];
  }
  return ["all roof systems"];
}

function hydrateExistingService(service: ExistingServicePageInput): ServicePage {
  return {
    ...service,
    id: service.slug,
    name: service.title,
    shortName: service.shortTitle,
    imageAlt: `${service.title} service by Revolin Roofing`,
    hero: {
      eyebrow: service.eyebrow,
      title: service.title,
      body: service.description,
    },
    benefits: service.methodology.slice(0, 3),
    cta: { label: "Request an assessment", path: "/contact" },
    existingService: true,
    newService: false,
    emergencyEligible: service.category === "emergency-repairs",
    plannedEligible: true,
    provinceWideEligible: true,
    customerTypes: ["homeowners"],
    roofSystems: inferRoofSystems(`${service.title} ${service.description}`),
    featured: FEATURED_SERVICE_SLUGS.includes(service.slug),
    enabled: true,
    relatedServices: [...service.relatedServiceSlugs],
  };
}

function defaultImageForDraft(draft: ServiceDraft): ServiceImageKey {
  if (draft.image) return draft.image;
  const systems = draft.roofSystems.join(" ").toLowerCase();
  if (systems.includes("metal") || systems.includes("sheet metal")) return "metal";
  if (
    systems.includes("flat") ||
    systems.includes("membrane") ||
    systems.includes("epdm") ||
    systems.includes("tpo") ||
    systems.includes("sbs")
  ) {
    return "flat";
  }
  if (systems.includes("shingle") || systems.includes("cedar")) return "shingles";
  return "hero";
}

function hydrateNewService(draft: ServiceDraft): ServicePage {
  const shortTitle = draft.shortTitle ?? draft.title;
  const path = `/services/${draft.slug}`;
  const benefits = draft.benefits ?? [
    "Assessment-led recommendations",
    "Written scope and clear next steps",
    "Materials selected for the building and exposure",
  ];
  const relatedServiceSlugs = [...(draft.relatedServiceSlugs ?? [])];

  return {
    id: draft.slug,
    slug: draft.slug,
    path,
    title: draft.title,
    shortTitle,
    name: draft.title,
    shortName: shortTitle,
    eyebrow: SERVICE_CATEGORY_LABELS[draft.category],
    category: draft.category,
    description: draft.description,
    definition: draft.description,
    metaTitle: `${draft.title} BC | Revolin Roofing`,
    metaDescription: draft.description,
    image: defaultImageForDraft(draft),
    imageAlt: `${draft.title} for a British Columbia property`,
    conceptImage: {
      src: `/media/new-service-concepts/${draft.slug}.jpg`,
      width: 1536,
      height: 1024,
      alt: `${draft.title} on a luxury British Columbia property`,
    },
    hero: {
      eyebrow: SERVICE_CATEGORY_LABELS[draft.category],
      title: draft.title,
      body: draft.description,
    },
    benefits,
    cta: { label: "Request an assessment", path: "/contact" },
    existingService: false,
    newService: true,
    emergencyEligible: draft.emergencyEligible ?? false,
    plannedEligible: draft.plannedEligible ?? true,
    provinceWideEligible: draft.provinceWideEligible ?? true,
    customerTypes: [...draft.customerTypes],
    roofSystems: [...draft.roofSystems],
    featured: false,
    // Publication is an explicit approval step; drafts default to unavailable.
    enabled: draft.enabled === true,
    relatedServices: relatedServiceSlugs,
    forWhom: `Property owners and project teams considering ${draft.title.toLowerCase()} with a scope matched to the building, roof system and site conditions.`,
    warningSigns: [],
    evaluates: [
      "Existing assembly and substrate condition",
      "Drainage, penetrations and transition details",
      "Access, safety and project sequencing requirements",
    ],
    causes: [],
    methodology: benefits,
    materials: [...draft.roofSystems],
    bcClimate:
      "The recommended scope must account for British Columbia rainfall, wind exposure, temperature changes and the building’s specific location.",
    whenAppropriate:
      "After an assessment confirms that this service matches the building condition, project goals and roof assembly.",
    whenOtherService:
      "If investigation identifies a different failure source or broader assembly issue, Revolin will recommend the more appropriate repair, maintenance or replacement service.",
    process: [
      "Review the property, roof system and project goals",
      "Complete a condition and access assessment",
      "Prepare a written scope with suitable system options",
      "Schedule and complete approved work",
      "Document completion and recommended maintenance",
    ],
    relatedServiceSlugs,
    relatedAreaSlugs: [],
    solutionCategories: [
      {
        title: "Assessment",
        body: "Confirm the existing condition and the correct service scope.",
      },
      {
        title: "Planning",
        body: "Select compatible materials and coordinate access and sequencing.",
      },
      { title: "Delivery", body: "Complete and document the approved work." },
    ],
    repairVsReplace: {
      repair:
        "Localized work may be appropriate when the surrounding assembly remains serviceable.",
      replace:
        "Broader renewal may be appropriate when deterioration is widespread or the assembly is at end of life.",
    },
    scopeFactors: [
      "Building size and roof geometry",
      "Existing condition and concealed damage",
      "System and material selection",
      "Access, staging and mobilization",
      "Project location and schedule",
    ],
    faqs: [
      {
        question: `What is included in ${draft.title.toLowerCase()}?`,
        answer: `${draft.description} The final scope is confirmed after Revolin reviews the property, assembly and access conditions.`,
      },
    ],
    publicPriceHint:
      "Pricing depends on area, access, existing condition, material selection, project location and the approved scope. Request an assessment for project-specific pricing.",
  };
}

const protectedExistingServicePages: ExistingServicePageInput[] = [
  {
    slug: "emergency-roof-leak-repair",
    title: "Emergency Roof Leak Repair",
    shortTitle: "Emergency Leak Repair",
    eyebrow: "Active water entry",
    category: "emergency-repairs",
    image: "emergency-roof-leak-repair",
    description:
      "Priority leak diagnosis - same-day when scheduling allows - with temporary or permanent localized repair when water is entering a Metro Vancouver home through the roof assembly.",
    definition:
      "Emergency roof leak repair is focused investigation and stabilization of active or rapidly developing water entry through a roof covering, flashing, penetration or related interface. Work may include temporary dry-in and permanent localized repair when conditions allow.",
    metaTitle: "Emergency Roof Leak Repair Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Roof leak diagnosis and localized emergency repair for Metro Vancouver homes, same-day when scheduling allows. Source-first assessment and clear next steps.",
    forWhom:
      "Homeowners with active dripping, sudden ceiling stains during rain, attic moisture after storms, or damage that needs safe diagnosis and containment before a larger scope is planned.",
    warningSigns: [
      "Active drip or steady dampness during or after rainfall",
      "New or expanding ceiling, wall or light-fixture stains",
      "Wet insulation, sheathing or attic framing",
      "Water tracking along valleys, chimneys, skylights or plumbing vents",
      "Interior damage progressing after each weather event",
    ],
    evaluates: [
      "Interior evidence and likely water path",
      "Roof covering condition around the affected zone",
      "Flashings, penetrations, valleys and wall interfaces",
      "Need for temporary protection versus permanent repair",
      "Whether adjacent materials can support durable work",
    ],
    causes: [
      "Failed flashing or pipe boot seals",
      "Wind-lifted or missing covering materials",
      "Debris-blocked drainage concentrating water",
      "Storm-damaged ridges, edges or transitions",
      "Prior patches that did not address the true source",
    ],
    methodology: [
      "Document interior and exterior evidence safely",
      "Trace the most probable entry path rather than only the stain location",
      "Stabilize with temporary dry-in when weather or access requires it",
      "Execute compatible localized repair when the surrounding assembly is serviceable",
      "Explain residual risk and whether broader work should follow",
    ],
    materials: [
      "Compatible covering materials for the existing system",
      "Underlayment or membrane patches where appropriate",
      "Flashing metals and penetration boots",
      "Sealants used only as part of a correct detail, not as the sole fix",
      "Temporary tarps or dry-in materials when needed",
    ],
    bcClimate:
      "Metro Vancouver’s frequent rain means small defects can produce repeated interior damage. Timely diagnosis matters more than a surface patch that leaves the true path unresolved.",
    whenAppropriate:
      "When water entry is localized, the surrounding roof remains serviceable, and a durable repair or temporary dry-in can be executed safely.",
    whenOtherService:
      "Widespread covering failure, multiple independent leaks, or end-of-life assemblies may require broader repair planning, storm dry-in only, or full replacement discussion.",
    relatedServiceSlugs: [
      "storm-tarping-emergency-dry-in",
      "flashing-roof-penetration-repair",
      "shingle-spot-repair",
      "skylight-leak-flashing-repair",
      "chimney-flashing-repair",
    ],
    relatedAreaSlugs: ["vancouver", "burnaby", "coquitlam"],
    solutionCategories: [
      {
        title: "Source diagnosis",
        body: "Connect interior evidence to the exterior detail most likely admitting water, including lateral travel under the covering.",
      },
      {
        title: "Temporary dry-in",
        body: "Stabilize openings after storm damage or when permanent materials cannot be completed in the same visit.",
      },
      {
        title: "Localized permanent repair",
        body: "Replace or re-detail the failed component with materials compatible with the surrounding roof system.",
      },
    ],
    repairVsReplace: {
      repair:
        "A focused repair is usually appropriate when one failure zone is identified and surrounding materials remain sound enough to accept compatible work.",
      replace:
        "Replacement enters the conversation when investigation shows broad deterioration, multiple independent failures, or an assembly that cannot support durable patches.",
    },
    scopeFactors: [
      "Access and roof height",
      "Weather window",
      "Extent of concealed damage",
      "Material compatibility",
      "Number of penetrations involved",
      "Need for temporary protection",
    ],
    publicPriceHint:
      "Emergency scope depends on access, weather during the work, how far water has travelled and whether temporary protection is needed before permanent repair. Request an assessment for project-specific pricing.",
    path: "/services/emergency-roof-leak-repair",
    process: [
      "Call or send details and photos of what you are seeing",
      "Triage and scheduling - same-day attendance when availability allows",
      "On-roof source diagnosis with interior and exterior documentation",
      "Temporary dry-in or permanent localized repair as conditions permit",
      "Written summary of findings, work completed and recommended next steps",
    ],
    faqs: [
      {
        question: "What is emergency roof leak repair?",
        answer:
          "Emergency roof leak repair is focused investigation and stabilization of active or rapidly developing water entry through a roof covering, flashing, penetration or related interface. Work may include temporary dry-in and permanent localized repair when conditions allow.",
      },
      {
        question: "Can a roof leak be fixed while it is still raining?",
        answer:
          "The safe first step in active rain is usually temporary dry-in - tarping or sealing the entry area - because most permanent roofing materials need dry conditions to bond correctly. Permanent repair is then completed in a suitable weather window.",
      },
      {
        question: "Why is my ceiling stain far from any visible roof damage?",
        answer:
          "Water often enters at one detail and travels along underlayment, framing or sheathing before showing inside. That is why diagnosis traces the probable entry path across the roof rather than patching directly above the stain.",
      },
      {
        question: "Do you offer same-day roof leak repair in Metro Vancouver?",
        answer:
          "Revolin aims to attend active leaks the same day when scheduling and daylight allow, but attendance times are not guaranteed. Sharing photos and a clear description of what you are seeing helps the triage call.",
      },
      {
        question: "Will my leak repair be temporary or permanent?",
        answer:
          "That depends on weather, material availability and what the investigation finds. When a durable localized repair can be executed safely on the first visit, it is. Otherwise the roof is stabilized with temporary dry-in and permanent work is scoped in writing.",
      },
      {
        question: "How much does an emergency roof leak repair cost?",
        answer:
          "Pricing depends on roof height and access, the extent of concealed damage, and whether temporary protection is required in addition to the repair. Revolin provides project-specific pricing after assessment rather than quoting sight-unseen.",
      },
    ],
  },
  {
    slug: "flashing-roof-penetration-repair",
    title: "Flashing / Roof Penetration Repair",
    shortTitle: "Flashing & Penetrations",
    eyebrow: "Transitions and interfaces",
    category: "emergency-repairs",
    image: "flashing-roof-penetration-repair",
    description:
      "Repair of chimney, wall, vent, pipe boot and other roof penetration flashings where water commonly enters Metro Vancouver roofs.",
    definition:
      "Flashing and roof penetration repair corrects water-shedding interfaces where the roof covering meets walls, chimneys, vents, pipes and other penetrations. These details are frequent leak sources when metalwork, boots or sealant joints fail.",
    metaTitle: "Flashing & Penetration Repair Vancouver | Revolin Roofing",
    metaDescription:
      "Chimney, wall, vent and pipe-boot flashing repair for Metro Vancouver roofs. Source-focused assessment and compatible re-detailing of roof penetrations.",
    forWhom:
      "Owners seeing leaks near chimneys, sidewalls, plumbing stacks, mechanical vents or other roof openings, or who have aging flashings that no longer shed water correctly.",
    warningSigns: [
      "Stains near chimney chases or exterior walls",
      "Cracked or hardened sealant at penetrations",
      "Lifted, rusted or separated step and counter-flashing",
      "Perished rubber pipe boots",
      "Water entry during wind-driven rain at wall-to-roof junctions",
    ],
    evaluates: [
      "Flashing geometry and material condition",
      "Compatibility with the surrounding roof covering",
      "Counter-flashing and wall interface integrity",
      "Penetration boot and collar condition",
      "Whether sealant is masking a failed metal detail",
    ],
    causes: [
      "Thermal movement opening joints",
      "UV and weather aging of boots and sealants",
      "Improper step or counter-flashing integration",
      "Corrosion or mechanical damage to metal flashings",
      "Roof covering replacement that did not re-detail penetrations correctly",
    ],
    methodology: [
      "Identify each penetration and wall interface in the failure zone",
      "Open only as needed to confirm the water path",
      "Replace failed boots, collars or metalwork with compatible details",
      "Re-integrate flashings with the field covering correctly",
      "Avoid relying on surface caulk as a permanent waterproofing strategy",
    ],
    materials: [
      "Step, counter and apron flashings",
      "Pipe boots and vent collars",
      "Compatible underlayment and ice/water membranes at critical interfaces",
      "Metal suitable for the coastal exposure and adjoining materials",
      "Manufacturer-compatible sealants used as secondary, not primary, protection",
    ],
    bcClimate:
      "Wind-driven rain and long wet seasons stress flashings and elastomer boots. Details that work in drier climates often fail earlier in coastal BC without correct metal integration.",
    whenAppropriate:
      "When the field roof covering is still serviceable and the leak is driven by a discrete flashing or penetration failure.",
    whenOtherService:
      "If the surrounding covering is broadly worn, or a chimney, skylight or low-slope assembly needs a different scope, those specialized services may be more appropriate.",
    relatedServiceSlugs: [
      "emergency-roof-leak-repair",
      "chimney-flashing-repair",
      "skylight-leak-flashing-repair",
      "shingle-spot-repair",
      "roof-health-check-maintenance",
    ],
    relatedAreaSlugs: ["vancouver", "north-vancouver", "burnaby"],
    solutionCategories: [
      {
        title: "Penetration boot replacement",
        body: "Replace aged pipe and vent boots before they crack and admit water around plumbing or mechanical openings.",
      },
      {
        title: "Wall and step flashing repair",
        body: "Restore wall-to-roof interfaces where step and counter-flashing no longer shed water.",
      },
      {
        title: "Chimney and complex interface work",
        body: "Address multi-plane flashing assemblies that require careful sequencing with the roof covering.",
      },
    ],
    repairVsReplace: {
      repair:
        "Flashing repair is appropriate when the roof field is sound and the failure is concentrated at an interface.",
      replace:
        "Field replacement may be needed if flashings must be fully re-integrated into a covering that is already at end of life.",
    },
    scopeFactors: [
      "Number and type of penetrations",
      "Wall cladding interface",
      "Metal material compatibility",
      "Access around chimneys and dormers",
      "Existing roof covering condition",
      "Need to open adjacent assemblies",
    ],
    path: "/services/flashing-roof-penetration-repair",
    process: [
      "Describe the leak location and any visible details around it",
      "Roof-level inspection of flashings and penetrations in the failure zone",
      "Written repair scope identifying each detail to be corrected",
      "Re-detailing with compatible metals, boots and membranes",
      "Final review of the corrected interfaces and surrounding covering",
    ],
    publicPriceHint:
      "Pricing reflects the number and type of penetrations, wall-interface complexity and how much of the surrounding covering must be opened and re-integrated. Request an assessment.",
    faqs: [
      {
        question: "What is flashing / roof penetration repair?",
        answer:
          "Flashing and roof penetration repair corrects water-shedding interfaces where the roof covering meets walls, chimneys, vents, pipes and other penetrations. These details are frequent leak sources when metalwork, boots or sealant joints fail.",
      },
      {
        question: "How do I know if my roof leak is a flashing problem?",
        answer:
          "Leaks that appear near chimneys, sidewalls, skylights, plumbing stacks or vents - especially during wind-driven rain - are commonly flashing-related. Interior stains that line up with a roof penetration above are a strong indicator, but confirmation requires roof-level inspection.",
      },
      {
        question: "Can flashing be repaired without replacing the roof?",
        answer:
          "Usually yes, when the surrounding covering is still serviceable. Individual flashings, boots and collars can be replaced and re-woven into the existing roof. If the field is at end of life, re-detailing is better combined with replacement planning.",
      },
      {
        question: "Why did caulking the flashing not stop my leak?",
        answer:
          "Sealant ages much faster than metal and cannot compensate for incorrect flashing geometry. Surface caulk often hides the failure while water keeps entering. A durable repair re-establishes the metal detail; sealant plays only a secondary role.",
      },
      {
        question: "How long do pipe boots and flashings last in coastal BC?",
        answer:
          "Service life varies with material, exposure and installation quality, so no single number is honest. Rubber pipe boots typically age faster than metal flashings because UV and temperature cycling harden the elastomer, and coastal wind-driven rain tends to expose weak details earlier than drier climates do.",
      },
    ],
  },
  {
    slug: "moss-removal-roof-gutter",
    title: "Moss Removal + Roof/Gutter Bundle",
    shortTitle: "Moss & Gutter Bundle",
    eyebrow: "Maintenance bundle",
    category: "maintenance",
    image: "moss-removal-roof-gutter",
    description:
      "Moss treatment, debris removal, gutter cleaning and visual roof inspection for Metro Vancouver homes where organic growth and blocked drainage accelerate roof wear.",
    definition:
      "A combined moss removal and roof/gutter maintenance service that addresses organic growth on roof surfaces, clears gutters and downspouts, and documents visible roof conditions for planning.",
    metaTitle: "Moss Removal & Gutter Cleaning Vancouver | Revolin Roofing",
    metaDescription:
      "Moss removal, debris clearing, gutter cleaning and visual roof inspection for Metro Vancouver homes. Maintenance focused on drainage and covering health.",
    forWhom:
      "Homeowners with moss, lichen or heavy debris on shingles or in gutters, especially under tree cover.",
    warningSigns: [
      "Moss mats holding moisture against shingles",
      "Overflowing gutters during ordinary rain",
      "Organic growth on north-facing slopes",
      "Debris dams in valleys",
      "Downspouts that no longer discharge freely",
    ],
    evaluates: [
      "Extent of moss and organic growth",
      "Covering condition beneath growth",
      "Gutter and downspout flow",
      "Valley and edge debris",
      "Whether damage already requires repair",
    ],
    causes: [
      "Shaded damp coastal conditions",
      "Tree debris",
      "Neglected gutter maintenance",
      "Surface texture holding organics",
      "Limited roof-plane drying in cool months",
    ],
    methodology: [
      "Protect landscaping and access",
      "Remove moss and debris using methods appropriate to the covering",
      "Clean gutters and flush downspouts",
      "Perform visual condition review",
      "Note repair priorities discovered during cleaning",
    ],
    materials: [
      "Soft cleaning tools appropriate to the roof type",
      "Approved treatment products when used",
      "Gutter scoops and flush equipment",
    ],
    bcClimate:
      "Lower Mainland shade and rainfall create ideal moss conditions. Growth traps moisture against coverings and can accelerate granule loss if ignored.",
    whenAppropriate:
      "When the roof covering is still fundamentally intact and growth, debris and drainage are the main issues.",
    whenOtherService:
      "Missing shingles, active leaks or failed flashings need repair services rather than cleaning alone.",
    relatedServiceSlugs: [
      "gutter-cleaning-downspout-flush",
      "roof-health-check-maintenance",
      "shingle-spot-repair",
      "gutter-guard-installation",
    ],
    relatedAreaSlugs: ["north-vancouver", "coquitlam", "burnaby"],
    solutionCategories: [
      {
        title: "Moss and organic removal",
        body: "Reduce moisture-holding growth on appropriate roof surfaces.",
      },
      {
        title: "Gutter and downspout cleaning",
        body: "Restore free drainage along eaves and discharge points.",
      },
      {
        title: "Visual roof review",
        body: "Note damaged coverings uncovered once debris is cleared.",
      },
    ],
    repairVsReplace: {
      repair: "Cleaning suits serviceable roofs where growth and debris are primary.",
      replace:
        "If cleaning reveals widespread brittleness, replacement planning may be more honest than repeated cleanings.",
    },
    scopeFactors: [
      "Roof area",
      "Pitch and access",
      "Tree cover density",
      "Gutter length",
      "Covering fragility",
      "Debris disposal",
    ],
    path: "/services/moss-removal-roof-gutter",
    process: [
      "Share roof type, shade exposure and access details",
      "Site review of growth extent, drainage and covering condition",
      "Written scope for cleaning, treatment and gutter work",
      "Careful removal, treatment where appropriate, and downspout flushing",
      "Condition notes with photos of anything that needs repair",
    ],
    publicPriceHint:
      "Pricing follows roof area, pitch, how established the growth is, gutter length and access. Request an assessment for project-specific pricing.",
    faqs: [
      {
        question: "What is moss removal + roof/gutter bundle?",
        answer:
          "A combined moss removal and roof/gutter maintenance service that addresses organic growth on roof surfaces, clears gutters and downspouts, and documents visible roof conditions for planning.",
      },
      {
        question: "Is pressure washing a good way to remove roof moss?",
        answer:
          "High-pressure washing is generally discouraged on asphalt shingles because it strips protective granules and can drive water under the covering. Moss is better removed with gentler mechanical methods and, where appropriate, treatments suited to the roofing material.",
      },
      {
        question: "Does moss actually damage a roof, or is it just cosmetic?",
        answer:
          "Established moss holds moisture against the covering, lifts shingle edges and accelerates granule loss. On cedar it keeps the wood damp and speeds decay. Removing growth early is far less costly than repairing the wear it causes.",
      },
      {
        question: "How often should Metro Vancouver roofs be de-mossed?",
        answer:
          "It depends on shade, tree cover and roof orientation: north-facing and heavily shaded slopes regrow fastest. Many local homes benefit from a yearly look and cleaning when growth starts to mat, rather than a fixed schedule.",
      },
      {
        question: "Will moss removal stop my gutters from overflowing?",
        answer:
          "Often the two go together: moss fragments and roof debris are common causes of clogged gutters and downspouts. This bundle clears both so drainage can be judged on its own merits. If gutters still overflow when clean, the metal or pitch may need attention.",
      },
    ],
  },
  {
    slug: "asphalt-shingle-replacement",
    title: "Asphalt Shingle Replacement",
    shortTitle: "Asphalt Replacement",
    eyebrow: "Steep-slope reroof",
    category: "replacement",
    image: "asphalt-shingle-replacement",
    description:
      "Complete asphalt shingle tear-off, underlayment, shingles, flashing integration and disposal for Metro Vancouver steep-slope homes.",
    definition:
      "Asphalt shingle replacement is a full steep-slope reroof that removes the existing shingle covering, addresses substrate and underlayment needs, installs a new shingle system and re-integrates flashings and penetrations.",
    metaTitle: "Asphalt Shingle Replacement Vancouver BC | Revolin Roofing",
    metaDescription:
      "Architectural asphalt shingle replacement for Metro Vancouver homes. Tear-off, underlayment, flashing integration and a written project scope after assessment.",
    forWhom:
      "Homeowners whose shingles are broadly worn, repeatedly repaired, or no longer a durable match for the home’s remaining service plan.",
    warningSigns: [
      "Curling, cracking or widespread granule loss",
      "Multiple repaired planes still failing",
      "Brittle shingles that no longer reseal",
      "Aging valleys, ridges and eaves",
      "Renovation work that makes a complete reroof coherent",
    ],
    evaluates: [
      "Existing layer count and deck condition",
      "Slope, valleys, dormers and geometry",
      "Ventilation and attic moisture evidence",
      "Flashing and penetration inventory",
      "Access, protection and disposal",
    ],
    causes: [
      "Normal end-of-life weathering",
      "Chronic moss and moisture",
      "Accumulated wind damage",
      "Poor ventilation accelerating aging",
      "Prior installations with weak detailing",
    ],
    methodology: [
      "Document conditions and options",
      "Plan tear-off, protection and disposal",
      "Repair deck as discovered and specified",
      "Install underlayment, flashings and field shingles in sequence",
      "Detail ridges, valleys, edges and penetrations as a complete assembly",
    ],
    materials: [
      "Architectural asphalt shingles",
      "Underlayment and ice/water protection where appropriate",
      "Valley, wall and penetration flashings",
      "Ridge and ventilation accessories",
      "Fasteners and edge components",
    ],
    bcClimate:
      "Coastal rain and moss growth make underlayment, valley detailing and ventilation especially important on Lower Mainland shingle roofs.",
    whenAppropriate:
      "When shingle deterioration is system-wide or continued patching leaves other near-term failures unresolved.",
    whenOtherService:
      "Isolated wind damage may still suit spot repair if the field is otherwise sound.",
    relatedServiceSlugs: [
      "shingle-spot-repair",
      "roof-health-check-maintenance",
      "roof-ventilation-upgrade",
      "fascia-soffit-repair-replacement",
      "gutter-replacement",
    ],
    relatedAreaSlugs: ["burnaby", "surrey", "langley"],
    solutionCategories: [
      {
        title: "Architectural laminate systems",
        body: "Dimensional residential shingles selected for geometry, exposure and design intent.",
      },
      {
        title: "Underlayment and water-shedding layers",
        body: "Coordinate field shingles with valleys, eaves and transitions.",
      },
      {
        title: "Flashing re-integration",
        body: "Walls, chimneys, vents and edges detailed as part of the new assembly.",
      },
    ],
    repairVsReplace: {
      repair:
        "Spot repair can serve when damage is limited and matching materials integrate cleanly.",
      replace:
        "Replacement is coherent when wear is widespread or the assembly cannot accept durable patches.",
    },
    scopeFactors: [
      "Roof area and pitch",
      "Layer count",
      "Deck repairs",
      "Valley and dormer complexity",
      "Material selection",
      "Access and disposal",
    ],
    path: "/services/asphalt-shingle-replacement",
    process: [
      "Assessment of covering, deck indicators and ventilation",
      "Written scope with material and detailing options",
      "Tear-off with property protection and disposal",
      "Deck repair, underlayment, flashing and shingle installation in sequence",
      "Final walkthrough and completion review",
    ],
    publicPriceHint:
      "Roof area, pitch, layer count, deck repairs, valley and dormer complexity and material selection drive the project price. Request an assessment for a written scope.",
    faqs: [
      {
        question: "What is asphalt shingle replacement?",
        answer:
          "Asphalt shingle replacement is a full steep-slope reroof that removes the existing shingle covering, addresses substrate and underlayment needs, installs a new shingle system and re-integrates flashings and penetrations.",
      },
      {
        question: "How long does an asphalt shingle replacement take?",
        answer:
          "Most single-family reroofs are measured in days rather than weeks, but duration depends on roof size, pitch, weather windows and how much deck repair is uncovered during tear-off. The written scope sets expectations before work begins.",
      },
      {
        question: "Can new shingles be installed over the old ones?",
        answer:
          "Overlays are sometimes technically possible, but tear-off is generally preferred: it allows deck inspection, proper underlayment and correct flashing integration, and it avoids stacking weight and trapping problems under the new covering.",
      },
      {
        question: "What happens if rotten decking is found during tear-off?",
        answer:
          "Discovered deck damage is documented and repaired before the new system goes on. A clear scope explains in advance how deck work is measured and priced, so mid-project findings are handled transparently rather than as surprises.",
      },
      {
        question: "Which shingles hold up best in Metro Vancouver weather?",
        answer:
          "Architectural laminate shingles are the common residential choice locally. In this climate, underlayment quality, valley and edge detailing and attic ventilation influence real-world service life at least as much as the shingle product itself.",
      },
    ],
  },
  {
    slug: "roof-health-check-maintenance",
    title: "Roof Health Check + Maintenance",
    shortTitle: "Health Check",
    eyebrow: "Condition first",
    category: "maintenance",
    image: "roof-health-check-maintenance",
    description:
      "Inspection, photographs, minor sealant or maintenance work and a roof condition report for informed planning.",
    definition:
      "A roof health check is a structured visual assessment of covering, flashings, drainage and accessible related components, typically paired with photo documentation and limited maintenance items agreed in advance.",
    metaTitle: "Roof Health Check & Maintenance Vancouver | Revolin Roofing",
    metaDescription:
      "Roof health check, photos and maintenance priorities for Metro Vancouver homeowners. Clear condition documentation before repair or replacement decisions.",
    forWhom:
      "Homeowners preparing to buy or sell, planning renovations, noticing early warning signs, or wanting a documented baseline before rainy season.",
    warningSigns: [
      "Uncertainty about roof age or remaining condition",
      "Minor stains without a clear source",
      "Seasonal debris and moss accumulation",
      "Aging sealant at penetrations",
      "Need for a written planning baseline",
    ],
    evaluates: [
      "Covering condition by plane",
      "Flashings and penetrations",
      "Drainage and gutter interfaces",
      "Visible ventilation components",
      "Priority ranking of maintenance versus repair items",
    ],
    causes: [
      "Normal aging without documentation",
      "Deferred maintenance",
      "Site-specific weather exposure",
      "Prior work of unknown quality",
      "Buyer or renovation decision needs",
    ],
    methodology: [
      "Review concerns and accessible history",
      "Inspect exterior roof zones systematically",
      "Photograph representative conditions",
      "Complete only agreed minor maintenance",
      "Deliver a clear condition summary and options",
    ],
    materials: [
      "Photo documentation",
      "Minor sealants or fasteners when included",
      "Cleaning of limited debris if scoped",
      "Written condition notes",
    ],
    bcClimate:
      "Seasonal rain makes pre-winter and post-storm health checks useful for catching blocked drainage, lifted materials and early flashing failures.",
    whenAppropriate:
      "When you need clarity before committing to repair, replacement or further investigation.",
    whenOtherService:
      "Active major leaks or storm openings should go to emergency or repair pathways.",
    relatedServiceSlugs: [
      "moss-removal-roof-gutter",
      "shingle-spot-repair",
      "flashing-roof-penetration-repair",
      "asphalt-shingle-replacement",
    ],
    relatedAreaSlugs: ["vancouver", "richmond", "port-moody"],
    solutionCategories: [
      {
        title: "Documented inspection",
        body: "A structured review of what is visible and accessible on the roof assembly.",
      },
      {
        title: "Minor maintenance",
        body: "Limited sealant or debris work only when included and appropriate.",
      },
      {
        title: "Planning summary",
        body: "Separate immediate concerns from medium-term monitoring items.",
      },
    ],
    repairVsReplace: {
      repair:
        "Many findings are maintenance or localized repair items rather than full replacement triggers.",
      replace:
        "Replacement is discussed only when the observed condition supports that broader path.",
    },
    scopeFactors: [
      "Roof size and complexity",
      "Access",
      "Photo documentation depth",
      "Included maintenance items",
      "Number of roof systems on the property",
    ],
    path: "/services/roof-health-check-maintenance",
    process: [
      "Book a health check and describe any concerns",
      "Systematic exterior inspection with photo documentation",
      "Agreed minor maintenance items completed during the visit",
      "Written condition summary with ranked priorities",
      "Follow-up discussion of any recommended repairs",
    ],
    publicPriceHint:
      "Health check scope follows roof size and complexity, access, and how much documentation and minor maintenance is included. Request an assessment.",
    faqs: [
      {
        question: "What is roof health check + maintenance?",
        answer:
          "A roof health check is a structured visual assessment of covering, flashings, drainage and accessible related components, typically paired with photo documentation and limited maintenance items agreed in advance.",
      },
      {
        question: "How often should I have my roof inspected?",
        answer:
          "A useful local rhythm is a check before the fall rains, plus after any major windstorm. Homes under heavy tree cover, or with roofs in the second half of their expected life, benefit from more frequent attention.",
      },
      {
        question: "What do I receive after a roof health check?",
        answer:
          "A photo-documented summary of covering, flashing, drainage and ventilation conditions, separated into items needing prompt repair, items to monitor and normal wear. It is a planning document you can act on at your own pace.",
      },
      {
        question: "Is a roof check worth it before buying or selling a home?",
        answer:
          "Yes - the roof is one of the costlier building components to renew, and a documented condition baseline reduces negotiation guesswork. Note this is a roofing condition review, not a formal pre-purchase home inspection.",
      },
      {
        question: "Do you fix problems found during the inspection?",
        answer:
          "Small agreed maintenance items - such as minor sealant renewal at penetrations - can be completed during the visit. Larger findings are documented and scoped separately so you can decide with clear information rather than on-the-spot pressure.",
      },
    ],
  },
  {
    slug: "shingle-spot-repair",
    title: "Shingle Spot Repair",
    shortTitle: "Shingle Spot",
    eyebrow: "Localized covering repair",
    category: "emergency-repairs",
    image: "shingle-spot-repair",
    description:
      "Missing, wind-damaged, cracked or otherwise damaged asphalt shingles repaired in contained areas when the surrounding field remains serviceable.",
    definition:
      "Shingle spot repair replaces or reseats a limited area of damaged asphalt shingles without a full reroof when matching and surrounding conditions allow.",
    metaTitle: "Shingle Spot Repair Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Repair missing, cracked or wind-damaged asphalt shingles on Metro Vancouver homes when the surrounding roof field remains serviceable.",
    forWhom: "Owners with isolated shingle damage on an otherwise sound steep-slope roof.",
    warningSigns: [
      "Missing shingles after wind",
      "Cracked tabs",
      "Exposed fasteners or underlayment in a limited zone",
      "Granule piles from one area",
      "Previous patches lifting",
    ],
    evaluates: [
      "Matchability of shingles",
      "Surrounding field flexibility",
      "Underlayment condition locally",
      "Exposure of the damaged plane",
      "Whether damage is truly isolated",
    ],
    causes: [
      "Wind uplift",
      "Impact damage",
      "Thermal cracking",
      "Foot traffic damage",
      "Age-related seal strip failure",
    ],
    methodology: [
      "Confirm matchability",
      "Remove damaged shingles carefully",
      "Replace underlayment locally if needed",
      "Install matching shingles with correct fastening",
      "Seal only as required by the detail",
    ],
    materials: [
      "Matching asphalt shingles",
      "Local underlayment",
      "Compatible fasteners",
      "Detail sealant only where specified",
    ],
    bcClimate:
      "Coastal wind events and wet winters make timely spot repairs important before water reaches the deck.",
    whenAppropriate: "When damage is limited and the field can accept compatible shingles.",
    whenOtherService:
      "Widespread brittleness or multi-plane failure points toward full asphalt replacement.",
    relatedServiceSlugs: [
      "asphalt-shingle-replacement",
      "flashing-roof-penetration-repair",
      "emergency-roof-leak-repair",
      "roof-health-check-maintenance",
    ],
    relatedAreaSlugs: ["surrey", "langley", "coquitlam"],
    solutionCategories: [
      {
        title: "Localized shingle replacement",
        body: "Restore a contained damage zone with compatible materials.",
      },
      {
        title: "Edge and ridge touch-up",
        body: "Correct discrete failures at high-stress edges when still isolated.",
      },
      {
        title: "Leak-path check",
        body: "Confirm the spot damage is not masking a flashing failure.",
      },
    ],
    repairVsReplace: {
      repair: "Spot repair when the field remains flexible and matchable.",
      replace: "Full replacement when wear is systemic.",
    },
    scopeFactors: [
      "Area of damage",
      "Pitch and access",
      "Material match",
      "Underlayment needs",
      "Weather window",
    ],
    path: "/services/shingle-spot-repair",
    process: [
      "Send photos of the damaged area if safely visible",
      "On-roof confirmation that damage is genuinely localized",
      "Match check for shingle profile and colour",
      "Careful removal and replacement of the affected shingles",
      "Review of the repair and the surrounding field",
    ],
    publicPriceHint:
      "Spot repair pricing turns on the damaged area, pitch and access, and how well replacement shingles can be matched. Request an assessment.",
    faqs: [
      {
        question: "What is shingle spot repair?",
        answer:
          "Shingle spot repair replaces or reseats a limited area of damaged asphalt shingles without a full reroof when matching and surrounding conditions allow.",
      },
      {
        question: "A few shingles blew off in a storm: is that urgent?",
        answer:
          "Treat it promptly. Missing shingles expose underlayment that is not designed for long-term weather exposure, and the next heavy rain can reach the deck. A quick localized repair usually prevents a much larger problem.",
      },
      {
        question: "Will replacement shingles match my existing roof?",
        answer:
          "New shingles rarely match weathered ones perfectly, because existing shingles fade and lose granules over time. Colour-appropriate product selection minimizes the contrast, and match expectations are discussed honestly before work proceeds.",
      },
      {
        question: "How many damaged shingles are too many for spot repair?",
        answer:
          "There is no fixed count: what matters is whether the damage is confined and whether the surrounding field is still flexible enough to accept work without cracking. Repeated failures across multiple planes usually signal that replacement planning is more economical.",
      },
      {
        question: "Can I just seal a cracked shingle instead of replacing it?",
        answer:
          "Surface sealant on a cracked shingle is short-lived and can complicate later repair. Replacing the damaged shingles restores the intended water-shedding overlap and is usually the more durable, cheaper-over-time fix.",
      },
    ],
  },
  {
    slug: "gutter-cleaning-downspout-flush",
    title: "Gutter Cleaning + Downspout Flush",
    shortTitle: "Gutter Cleaning",
    eyebrow: "Drainage maintenance",
    category: "gutters-exterior",
    image: "gutter-cleaning-downspout-flush",
    description:
      "Clean gutters and flush downspouts so eave drainage works during Metro Vancouver rainfall.",
    definition:
      "Gutter cleaning removes debris from gutters and downspouts so roof runoff can leave the eave line without overflow into fascia, foundations or landscaping.",
    metaTitle: "Gutter Cleaning & Downspouts Vancouver BC | Revolin Roofing",
    metaDescription:
      "Professional gutter cleaning and downspout flushing for Metro Vancouver homes. Restore eave drainage and reduce overflow risk.",
    forWhom:
      "Homeowners seeing overflow, plant growth in gutters, or staining on fascia after ordinary rain.",
    warningSigns: [
      "Water sheeting over gutter faces",
      "Plants growing in troughs",
      "Foundation splashback",
      "Stained fascia",
      "Downspouts that do not drain when tested",
    ],
    evaluates: [
      "Debris volume",
      "Trough pitch",
      "Outlet and extension condition",
      "Fascia attachment",
      "Whether metal has already failed",
    ],
    causes: [
      "Leaf and moss debris",
      "Needle accumulation from conifers",
      "Settled pitch",
      "Clogged screens",
      "Missed seasonal cleaning",
    ],
    methodology: [
      "Clear troughs",
      "Flush downspouts",
      "Check outlets and extensions",
      "Note metal condition",
      "Recommend guards or replacement only if justified",
    ],
    materials: ["Cleaning tools", "Flush equipment", "Basic fasteners only if scoped"],
    bcClimate:
      "Frequent rain means clogged gutters quickly wet fascia and foundations in the Lower Mainland.",
    whenAppropriate: "When metal is still sound and debris is the main problem.",
    whenOtherService: "Rusted, separated or chronically undersized gutters may need replacement.",
    relatedServiceSlugs: [
      "moss-removal-roof-gutter",
      "gutter-replacement",
      "gutter-guard-installation",
      "fascia-soffit-repair-replacement",
    ],
    relatedAreaSlugs: ["vancouver", "burnaby", "north-vancouver"],
    solutionCategories: [
      {
        title: "Trough cleaning",
        body: "Remove organic and granular debris from gutters.",
      },
      {
        title: "Downspout flush",
        body: "Restore continuous discharge paths.",
      },
      {
        title: "Condition notes",
        body: "Flag failed metal or fascia issues discovered during cleaning.",
      },
    ],
    repairVsReplace: {
      repair: "Cleaning when troughs remain structurally sound.",
      replace: "Replacement when metal or pitch cannot perform after cleaning.",
    },
    scopeFactors: [
      "Linear footage",
      "Storeys and access",
      "Debris type",
      "Downspout count",
      "Safety requirements",
    ],
    path: "/services/gutter-cleaning-downspout-flush",
    process: [
      "Share home height, gutter length and access notes",
      "Clear troughs of leaves, needles and granular debris",
      "Flush each downspout and confirm free discharge",
      "Note metal, pitch and fascia condition while working",
      "Report anything cleaning alone cannot fix",
    ],
    publicPriceHint:
      "Pricing generally follows linear footage, number of storeys, debris load and safety and access requirements. Request an assessment.",
    faqs: [
      {
        question: "What is gutter cleaning + downspout flush?",
        answer:
          "Gutter cleaning removes debris from gutters and downspouts so roof runoff can leave the eave line without overflow into fascia, foundations or landscaping.",
      },
      {
        question: "How often should gutters be cleaned in Metro Vancouver?",
        answer:
          "Most local homes need at least an autumn cleaning after leaf fall; properties under conifers often need a second visit because needles accumulate year-round. Overflow during ordinary rain is the sign you have waited too long.",
      },
      {
        question: "What happens if I skip gutter cleaning?",
        answer:
          "Blocked gutters overflow against fascia and siding, dump water at the foundation and can back water under the roof edge. In this climate that regularly turns a small maintenance task into fascia rot or drainage repairs.",
      },
      {
        question: "Do you flush the downspouts or just clear the gutters?",
        answer:
          "Both. A clean trough that drains into a blocked downspout still overflows. Each downspout is flushed and its discharge confirmed, because lower-elbow and underground clogs are among the most common hidden failures.",
      },
      {
        question: "My gutters overflow even when they are clean: what then?",
        answer:
          "That points to a pitch, sizing or outlet problem rather than debris. Cleaning visits include condition notes, and if the metal or geometry cannot perform, repair or replacement is recommended with the reasons documented.",
      },
    ],
  },
  {
    slug: "storm-tarping-emergency-dry-in",
    title: "Storm Tarping / Emergency Dry-In",
    shortTitle: "Storm Dry-In",
    eyebrow: "Temporary waterproofing",
    category: "emergency-repairs",
    image: "storm-tarping-emergency-dry-in",
    description:
      "Temporary waterproofing after wind, tree or storm damage until permanent roof work can proceed.",
    definition:
      "Storm tarping and emergency dry-in temporarily protect openings or damaged roof areas from weather so interior damage can be limited while permanent repairs are planned.",
    metaTitle: "Storm Tarping & Emergency Dry-In Vancouver | Revolin Roofing",
    metaDescription:
      "Emergency roof tarping and temporary dry-in after storm or tree damage in Metro Vancouver. Stabilize openings until permanent repair is planned.",
    forWhom: "Owners with open deck, tree impact, or large missing field areas after storms.",
    warningSigns: [
      "Open roof deck after tree impact",
      "Large missing field areas",
      "Interior rain entry through a new opening",
      "Unstable debris on the roof",
      "Need for temporary protection before permanent materials",
    ],
    evaluates: [
      "Safety and access",
      "Opening size and location",
      "Interior risk",
      "Weather window",
      "Path to permanent repair",
    ],
    causes: [
      "Windstorms",
      "Tree failure",
      "Sudden covering loss",
      "Impact damage",
      "Partial failure of aged materials",
    ],
    methodology: [
      "Safety assessment first",
      "Clear only what is required for safe dry-in",
      "Install temporary waterproofing",
      "Document conditions",
      "Plan permanent repair path",
    ],
    materials: [
      "Tarps and temporary membranes",
      "Secure fastening appropriate to temporary work",
      "Protection for openings and edges",
    ],
    bcClimate:
      "Pacific storms can open roofs quickly; temporary dry-in limits secondary interior damage while permanent scope is defined.",
    whenAppropriate:
      "When an opening or unstable area must be weatherproofed before permanent work.",
    whenOtherService:
      "Dry-in is not a permanent substitute for repair or replacement of the damaged assembly.",
    relatedServiceSlugs: [
      "emergency-roof-leak-repair",
      "shingle-spot-repair",
      "asphalt-shingle-replacement",
      "flat-low-slope-roof-repair",
    ],
    relatedAreaSlugs: ["vancouver", "richmond", "delta"],
    solutionCategories: [
      {
        title: "Temporary waterproofing",
        body: "Limit weather entry through storm openings.",
      },
      {
        title: "Stabilization",
        body: "Secure loose materials that threaten further damage.",
      },
      {
        title: "Documentation",
        body: "Record conditions to support permanent repair planning.",
      },
    ],
    repairVsReplace: {
      repair: "Temporary dry-in first when permanent work cannot be completed immediately.",
      replace:
        "Permanent repair or replacement follows once the assembly can be restored correctly.",
    },
    scopeFactors: [
      "Opening size",
      "Access and height",
      "Weather",
      "Interior exposure",
      "Debris hazards",
    ],
    path: "/services/storm-tarping-emergency-dry-in",
    process: [
      "Contact with photos and safety details if available",
      "Hazard and access assessment on site",
      "Stabilize debris only as needed for safe coverage",
      "Secure temporary waterproofing over the opening",
      "Document conditions and plan the permanent repair",
    ],
    publicPriceHint:
      "Dry-in scope depends on opening size, roof height, weather conditions during the work and any debris hazards. Request an assessment.",
    faqs: [
      {
        question: "What is storm tarping / emergency dry-in?",
        answer:
          "Storm tarping and emergency dry-in temporarily protect openings or damaged roof areas from weather so interior damage can be limited while permanent repairs are planned.",
      },
      {
        question: "A tree hit my roof: what should I do first?",
        answer:
          "Keep people out of rooms below the damage, stay off the roof, and photograph conditions from the ground and inside. Then arrange professional dry-in: unstable limbs and hidden structural damage make storm-damaged roofs hazardous.",
      },
      {
        question: "How long can a roof tarp stay on?",
        answer:
          "Tarps are temporary weather protection, not roofing. Their practical life is measured in weeks to a few months depending on exposure, fastening and winter storms. Permanent repair should be scheduled as soon as conditions and materials allow.",
      },
      {
        question: "Will my insurance cover emergency roof tarping?",
        answer:
          "Many property policies treat reasonable temporary protection after sudden damage as a covered mitigation step, but coverage is policy-specific. Revolin documents the conditions and work performed, which supports a claim, but cannot advise on your policy terms.",
      },
      {
        question: "Is a dry-in a substitute for repairing the roof?",
        answer:
          "No. Dry-in limits interior damage while a proper repair or replacement is planned. Leaving temporary protection in place long term usually leads to progressive water entry as fastenings and materials degrade.",
      },
    ],
  },
  {
    slug: "skylight-leak-flashing-repair",
    title: "Skylight Leak / Flashing Repair",
    shortTitle: "Skylight Repair",
    eyebrow: "Skylight interfaces",
    category: "emergency-repairs",
    image: "skylight-leak-flashing-repair",
    description: "Repair or reflash leaking skylights and the roofing that surrounds them.",
    definition:
      "Skylight leak and flashing repair addresses water entry at skylight curbs, flashings and adjacent roofing rather than assuming the glass alone is the cause.",
    metaTitle: "Skylight Leak & Flashing Repair Vancouver | Revolin Roofing",
    metaDescription:
      "Skylight leak diagnosis and curb flashing repair for Metro Vancouver homes. Correct roof interfaces around skylights, not just glass seals.",
    forWhom:
      "Owners with stains under skylights, failed curb flashings, or uncertainty whether condensation or a true leak is occurring.",
    warningSigns: [
      "Stains under skylight wells",
      "Peeling paint at light shafts",
      "Daylight at curb corners",
      "Failed step flashing beside units",
      "Standing water against curbs on low-slope roofs",
    ],
    evaluates: [
      "Skylight type and age",
      "Curb and flashing condition",
      "Adjacent covering integration",
      "Interior condensation patterns",
      "Whether unit replacement is required",
    ],
    causes: [
      "Curb flashing failure",
      "Incorrect integration with shingles or membrane",
      "Aged seals",
      "Condensation misdiagnosed as leaks",
      "Movement at framed openings",
    ],
    methodology: [
      "Differentiate leak versus condensation where possible",
      "Inspect curb flashings and adjacent roofing",
      "Reflash or repair compatible details",
      "Address surrounding covering as needed",
      "Recommend unit replacement only when justified",
    ],
    materials: [
      "Curb flashings",
      "Compatible underlayment or membrane details",
      "Surrounding shingles or membrane patches",
      "Sealants as secondary measures only",
    ],
    bcClimate:
      "Wind-driven rain finds weak skylight curbs quickly in coastal BC; glass condition alone rarely explains every leak.",
    whenAppropriate:
      "When the skylight unit is serviceable and the failure is in flashing or adjacent roofing.",
    whenOtherService:
      "Failed frames, chronically leaking sealed units or broader roof failure may require different scopes.",
    relatedServiceSlugs: [
      "flashing-roof-penetration-repair",
      "emergency-roof-leak-repair",
      "asphalt-shingle-replacement",
      "flat-low-slope-roof-repair",
    ],
    relatedAreaSlugs: ["west-vancouver", "vancouver", "north-vancouver"],
    solutionCategories: [
      {
        title: "Curb reflashing",
        body: "Restore metal and membrane interfaces at the skylight curb.",
      },
      {
        title: "Adjacent covering repair",
        body: "Correct shingles or membrane that no longer integrate with the unit.",
      },
      {
        title: "Leak versus condensation review",
        body: "Help separate assembly leaks from interior moisture symptoms.",
      },
    ],
    repairVsReplace: {
      repair: "Repair when the unit and curb can accept durable re-detailing.",
      replace: "Unit or roof replacement when the assembly cannot be made reliable.",
    },
    scopeFactors: [
      "Skylight count and size",
      "Roof system type",
      "Curb height and condition",
      "Access",
      "Interior finishes at risk",
    ],
    path: "/services/skylight-leak-flashing-repair",
    process: [
      "Describe the stain pattern and skylight age if known",
      "Inspect the curb, flashings and adjacent roofing",
      "Distinguish leak evidence from condensation",
      "Reflash or repair the failed details",
      "Verify the interface and document the work",
    ],
    publicPriceHint:
      "Skylight work is priced per unit context: curb condition, roof system type, flashing requirements and access. Request an assessment.",
    faqs: [
      {
        question: "What is skylight leak / flashing repair?",
        answer:
          "Skylight leak and flashing repair addresses water entry at skylight curbs, flashings and adjacent roofing rather than assuming the glass alone is the cause.",
      },
      {
        question: "Is my skylight leaking, or is it condensation?",
        answer:
          "Condensation appears as fine droplets or fogging on the glass and frame, typically in cold weather and humid rooms. Leaks track from curb corners or the top edge and often stain the drywall in the light shaft. Roof-level diagnosis confirms which is happening.",
      },
      {
        question: "Does a leaking skylight mean I need a new skylight?",
        answer:
          "Not necessarily. Many skylight leaks originate in the flashing or the surrounding roofing rather than the unit itself. If the glazing seal has failed - persistent fogging between panes - or the frame is deteriorated, replacement is discussed; otherwise reflashing usually resolves it.",
      },
      {
        question: "Should skylights be replaced during a reroof?",
        answer:
          "It is often the practical moment: the flashing must be redone anyway, access is already in place, and an aged unit is unlikely to outlive the new roof around it. A unit in good condition can be kept and correctly reflashed instead.",
      },
      {
        question: "Why does my skylight only leak in wind-driven rain?",
        answer:
          "Wind-pushed rain loads flashing details differently than falling rain: it can climb laps and enter details that shed vertical water perfectly well. That pattern strongly suggests a flashing geometry issue rather than a glass seal failure.",
      },
    ],
  },
  {
    slug: "chimney-flashing-repair",
    title: "Chimney Flashing Repair",
    shortTitle: "Chimney Flashing",
    eyebrow: "Chimney interfaces",
    category: "emergency-repairs",
    image: "chimney-flashing-repair",
    description:
      "Step flashing, counter-flashing and chimney-related leak correction on Metro Vancouver roofs.",
    definition:
      "Chimney flashing repair restores the metal interfaces that shed water where a masonry or framed chimney meets the roof planes.",
    metaTitle: "Chimney Flashing Repair Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Chimney step and counter-flashing repair for Metro Vancouver roofs. Correct chimney-to-roof leak paths with proper metal detailing.",
    forWhom:
      "Owners with leaks at chimney corners, rusted counter-flashing, or deteriorated cricket details.",
    warningSigns: [
      "Corner stains at chimney",
      "Rust streaks from counter-flashing",
      "Mortar issues with roof leaks below",
      "Missing cricket on wide chimneys",
      "Prior tar smeared at chimney base",
    ],
    evaluates: [
      "Chimney size and geometry",
      "Step and counter-flashing condition",
      "Upslope cricket needs",
      "Masonry interfaces",
      "Surrounding roof covering condition",
    ],
    causes: [
      "Step flashing not woven correctly",
      "Counter-flashing pulled from masonry",
      "Thermal movement",
      "Missing upslope cricket",
      "Age and corrosion",
    ],
    methodology: [
      "Inspect all chimney faces and roof intersections",
      "Open only as required to re-establish metal courses",
      "Install step and counter-flashing correctly",
      "Add or restore cricket details when needed",
      "Re-integrate surrounding covering",
    ],
    materials: [
      "Step and counter-flashings",
      "Cricket framing/covering materials when required",
      "Compatible underlayment",
      "Mortars or sealants only as appropriate secondary details",
    ],
    bcClimate:
      "Chimney corners are high-stress wet zones in wind-driven Lower Mainland rain; surface tar is not a lasting substitute for metal courses.",
    whenAppropriate: "When the chimney and surrounding roof field can support correct reflashing.",
    whenOtherService:
      "If the field roof is at end of life, reflashing is often best combined with replacement planning.",
    relatedServiceSlugs: [
      "flashing-roof-penetration-repair",
      "emergency-roof-leak-repair",
      "asphalt-shingle-replacement",
      "roof-health-check-maintenance",
    ],
    relatedAreaSlugs: ["vancouver", "burnaby", "new-westminster"],
    solutionCategories: [
      {
        title: "Step flashing restoration",
        body: "Re-establish woven metal courses with the roof covering.",
      },
      {
        title: "Counter-flashing correction",
        body: "Restore upper metal termination into masonry or cladding details.",
      },
      {
        title: "Cricket and diversion details",
        body: "Improve upslope water diversion on wide chimneys when required.",
      },
    ],
    repairVsReplace: {
      repair: "Chimney reflashing when the roof field remains serviceable.",
      replace: "Combine with reroof when the field cannot support durable integration.",
    },
    scopeFactors: [
      "Chimney width",
      "Roof pitch",
      "Masonry condition",
      "Access",
      "Surrounding covering age",
    ],
    path: "/services/chimney-flashing-repair",
    process: [
      "Share photos of the chimney and any interior stains",
      "Inspect all chimney faces and roof intersections",
      "Written scope for the metal courses to be rebuilt",
      "Install step, counter and cricket details correctly",
      "Re-integrate the covering and review the result",
    ],
    publicPriceHint:
      "Chimney work is priced around chimney width, roof pitch, masonry condition and how much surrounding covering must be lifted and re-integrated. Request an assessment.",
    faqs: [
      {
        question: "What is chimney flashing repair?",
        answer:
          "Chimney flashing repair restores the metal interfaces that shed water where a masonry or framed chimney meets the roof planes.",
      },
      {
        question: "Why does my chimney leak even though the roof is new?",
        answer:
          "Chimney flashing is a common weak point after reroofs: if counter-flashing was reused or step flashing was not properly rewoven, the new covering sheds water straight into an old failure. The chimney interface has to be detailed as its own assembly.",
      },
      {
        question: "What is counter-flashing and why does it matter?",
        answer:
          "Step flashing carries water over the shingles; counter-flashing is the upper metal course let into the masonry that covers the step flashing's vertical leg. Without a sound counter-flashing termination, water running down the chimney face goes straight behind the step metal.",
      },
      {
        question: "Do I need a cricket behind my chimney?",
        answer:
          "Wide chimneys on the downslope side collect water and debris against their upslope face. A cricket - a small diverting ridge - sheds that water around the chimney. Whether one is warranted depends on chimney width, roof pitch and observed conditions.",
      },
      {
        question: "Is roofing tar an acceptable chimney flashing fix?",
        answer:
          "As an emergency stopgap, tar can slow a leak. As a repair, it fails: it shrinks, cracks and traps water behind it while hiding the metal failure underneath. A lasting correction re-establishes the metal courses.",
      },
    ],
  },
  {
    slug: "gutter-replacement",
    title: "Gutter Replacement",
    shortTitle: "Gutter Replacement",
    eyebrow: "Eave drainage systems",
    category: "gutters-exterior",
    image: "gutter-replacement",
    description: "Seamless aluminum gutter and downspout replacement for reliable eave drainage.",
    definition:
      "Gutter replacement installs new gutter and downspout runs sized and pitched to carry roof runoff away from the building envelope.",
    metaTitle: "Gutter Replacement Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Seamless aluminum gutter and downspout replacement for Metro Vancouver homes. Correct pitch, outlets and eave drainage performance.",
    forWhom:
      "Owners with chronic overflow after cleaning, rusted or separated gutters, or fascia damage from failed drainage.",
    warningSigns: [
      "Seams splitting",
      "Gutters pulling from fascia",
      "Chronic overflow after cleaning",
      "Corrosion holes",
      "Incorrect pitch that cannot be corrected in place",
    ],
    evaluates: [
      "Linear footage and storeys",
      "Outlet design",
      "Fascia condition",
      "Colour and profile",
      "Downspout routing",
    ],
    causes: [
      "End of metal service life",
      "Undersized troughs",
      "Fascia movement",
      "Debris loading",
      "Poor original installation",
    ],
    methodology: [
      "Confirm fascia readiness",
      "Layout pitch and outlets",
      "Install seamless runs and downspouts",
      "Test flow paths",
      "Address splash and discharge points",
    ],
    materials: [
      "Seamless aluminum gutters",
      "Downspouts and elbows",
      "Hangers and fasteners",
      "End caps and outlets",
    ],
    bcClimate:
      "High rainfall volumes make correct gutter sizing and pitch essential for Lower Mainland homes.",
    whenAppropriate: "When existing gutters cannot perform even after cleaning and minor repair.",
    whenOtherService: "If troughs are sound, cleaning or guards may be enough.",
    relatedServiceSlugs: [
      "gutter-cleaning-downspout-flush",
      "gutter-guard-installation",
      "fascia-soffit-repair-replacement",
      "asphalt-shingle-replacement",
    ],
    relatedAreaSlugs: ["surrey", "langley", "maple-ridge"],
    solutionCategories: [
      {
        title: "Seamless eave systems",
        body: "New troughs pitched to controlled outlets.",
      },
      {
        title: "Downspout design",
        body: "Route discharge away from foundations and walkways.",
      },
      {
        title: "Fascia coordination",
        body: "Address soft fascia before hanging new metal when required.",
      },
    ],
    repairVsReplace: {
      repair: "Replace when metal or geometry has failed.",
      replace: "Clean or repair only when the system remains sound.",
    },
    scopeFactors: [
      "Linear footage",
      "Height/access",
      "Outlet count",
      "Colour selection",
      "Fascia repairs",
    ],
    path: "/services/gutter-replacement",
    process: [
      "Measure runs, outlets and downspout routing",
      "Confirm fascia can support new hangers",
      "Written scope with profile and colour selection",
      "On-site forming and installation of seamless runs",
      "Flow testing and discharge review",
    ],
    publicPriceHint:
      "Replacement pricing follows linear footage, storeys and access, outlet count and any fascia repairs needed first. Request an assessment.",
    faqs: [
      {
        question: "What is gutter replacement?",
        answer:
          "Gutter replacement installs new gutter and downspout runs sized and pitched to carry roof runoff away from the building envelope.",
      },
      {
        question: "What does a seamless gutter actually mean?",
        answer:
          "Seamless gutters are roll-formed on site in continuous lengths matched to each run, so joints occur only at corners and outlets. Fewer seams mean fewer future leak points compared with sectional gutters joined every few feet.",
      },
      {
        question: "Should gutters be replaced at the same time as the roof?",
        answer:
          "If both are near end of life, sequencing them together avoids damaging new gutters during tear-off and lets drip edge and trough position be coordinated. A sound gutter system does not need replacing just because the roof is.",
      },
      {
        question: "What size of gutters does a Metro Vancouver home need?",
        answer:
          "Sizing depends on the roof area draining to each run, pitch and outlet placement rather than one universal answer. Local rainfall intensity exposes undersized or under-drained runs quickly, so layout is assessed per home.",
      },
      {
        question: "How do you keep downspout water away from the foundation?",
        answer:
          "Discharge is planned toward appropriate drainage - extensions, splash management or existing ground drainage - so roof water is not deposited against the basement. Site grading and the property's drainage arrangements shape the right answer.",
      },
    ],
  },
  {
    slug: "gutter-guard-installation",
    title: "Gutter Guard Installation",
    shortTitle: "Gutter Guards",
    eyebrow: "Debris protection",
    category: "gutters-exterior",
    image: "gutter-guard-installation",
    description:
      "Leaf, moss and debris protection systems that reduce clog frequency on tree-lined properties.",
    definition:
      "Gutter guard installation adds a protection system over gutters to reduce leaf and debris accumulation while still allowing water to enter the trough.",
    metaTitle: "Gutter Guard Installation Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Gutter guard installation for Metro Vancouver homes with heavy tree debris. Reduce clog frequency on sound gutter systems.",
    forWhom:
      "Owners needing frequent cleaning, facing difficult multi-storey access, or living under heavy tree cover.",
    warningSigns: [
      "Cleaning required many times per year",
      "Difficult multi-storey access",
      "Needle-heavy tree cover",
      "Downspout clogs soon after cleaning",
      "Maintenance access limits",
    ],
    evaluates: [
      "Gutter structural condition first",
      "Debris profile",
      "Product suitability",
      "Roof edge geometry",
      "Maintenance expectations",
    ],
    causes: [
      "Dense landscaping",
      "Roof geometry dumping debris into troughs",
      "Fine debris defeating simple screens",
      "Inconsistent maintenance intervals",
    ],
    methodology: [
      "Confirm gutters are sound",
      "Select an appropriate protection system",
      "Install with attention to water entry and edges",
      "Test flow",
      "Explain residual maintenance needs",
    ],
    materials: [
      "Guard system components",
      "Fasteners compatible with gutters",
      "Edge accessories as required",
    ],
    bcClimate:
      "Coastal trees drop needles and moss fragments that defeat infrequent cleaning schedules; guards reduce but do not eliminate all maintenance.",
    whenAppropriate: "When gutters are structurally sound and debris load is the main problem.",
    whenOtherService: "Failed gutters should be replaced before guards are installed.",
    relatedServiceSlugs: [
      "gutter-cleaning-downspout-flush",
      "gutter-replacement",
      "moss-removal-roof-gutter",
    ],
    relatedAreaSlugs: ["coquitlam", "port-moody", "north-vancouver"],
    solutionCategories: [
      {
        title: "Debris protection systems",
        body: "Reduce leaf and needle entry into troughs.",
      },
      {
        title: "Compatibility check",
        body: "Install only on gutters that can still drain correctly.",
      },
      {
        title: "Maintenance expectations",
        body: "Clarify what guards reduce versus what still needs periodic attention.",
      },
    ],
    repairVsReplace: {
      repair: "Install guards on sound gutters with high debris load.",
      replace: "Replace gutters first when metal has failed.",
    },
    scopeFactors: [
      "Linear footage",
      "Product type",
      "Roof debris profile",
      "Access",
      "Existing gutter condition",
    ],
    path: "/services/gutter-guard-installation",
    process: [
      "Assess gutter condition and the property's debris profile",
      "Recommend a guard type suited to the surrounding trees",
      "Written scope including any pre-cleaning required",
      "Install guards with attention to water entry and edges",
      "Explain the remaining maintenance rhythm honestly",
    ],
    publicPriceHint:
      "Guard pricing depends on product type, linear footage, roof-edge geometry and the condition of the existing gutters. Request an assessment.",
    faqs: [
      {
        question: "What is gutter guard installation?",
        answer:
          "Gutter guard installation adds a protection system over gutters to reduce leaf and debris accumulation while still allowing water to enter the trough.",
      },
      {
        question: "Do gutter guards mean I never clean my gutters again?",
        answer:
          "No. Guards significantly reduce how often cleaning is needed but do not eliminate maintenance: debris accumulates on top of guards, valleys still concentrate material, and outlets should be checked periodically. The honest framing is fewer cleanings, not zero.",
      },
      {
        question: "Do gutter guards work with pine needles?",
        answer:
          "Fine needles defeat simple perforated screens. Micro-mesh products perform better under conifers, though a film of fine debris can still build on top and need occasional brushing. Product selection starts from what actually falls on your roof.",
      },
      {
        question: "Can guards be installed on my existing gutters?",
        answer:
          "Yes, if the gutters are structurally sound, correctly pitched and properly attached. Installing guards over failing gutters locks in the underlying problem, so condition is verified first - sometimes with a cleaning and minor refit.",
      },
      {
        question: "Will gutter guards cause problems in heavy rain?",
        answer:
          "A properly selected and installed system passes local rain volumes. Poorly matched guards can cause overshoot in intense downpours, especially below steep roof planes, which is why roof pitch and water velocity at the eave inform the choice.",
      },
    ],
  },
  {
    slug: "roof-ventilation-upgrade",
    title: "Roof Ventilation Upgrade",
    shortTitle: "Ventilation Upgrade",
    eyebrow: "Attic airflow",
    category: "gutters-exterior",
    image: "roof-ventilation-upgrade",
    description:
      "Box vents, ridge vents, soffit intake and attic airflow correction to support roof and attic performance.",
    definition:
      "Roof ventilation upgrades improve intake and exhaust pathways so attic spaces can manage moisture and heat more effectively when the existing balance is inadequate.",
    metaTitle: "Roof Ventilation Upgrade Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Roof and attic ventilation upgrades for Metro Vancouver homes. Balance soffit intake and exhaust for moisture and heat management.",
    forWhom:
      "Owners with attic condensation, uneven shingle aging, blocked soffits or inadequate exhaust.",
    warningSigns: [
      "Attic condensation or frost",
      "Musty attic odours",
      "Uneven shingle aging",
      "Blocked or painted-over soffit intake",
      "Hot attic contributing to moisture issues",
    ],
    evaluates: [
      "Attic configuration",
      "Existing exhaust vents",
      "Intake path continuity",
      "Insulation placement at eaves",
      "Roof geometry constraints",
    ],
    causes: [
      "Insufficient exhaust",
      "Blocked intake",
      "Insulation covering soffits",
      "Bath or kitchen fans dumping into attic",
      "Prior reroof without ventilation design",
    ],
    methodology: [
      "Assess intake and exhaust balance",
      "Clear or restore soffit intake where feasible",
      "Add or correct exhaust components",
      "Avoid over-venting without intake",
      "Coordinate with insulation and air-sealing discussions when needed",
    ],
    materials: [
      "Ridge, box or other exhaust vents as specified",
      "Soffit intake components",
      "Baffles or related attic accessories",
      "Flashings for new penetrations",
    ],
    bcClimate:
      "Cool, damp BC weather makes attic moisture management important; ventilation is one part of that system, not a cure-all for leaks.",
    whenAppropriate:
      "When airflow imbalance contributes to attic moisture or accelerated covering wear.",
    whenOtherService:
      "Ventilation upgrades do not replace leak repair or address all condensation sources.",
    relatedServiceSlugs: [
      "roof-health-check-maintenance",
      "asphalt-shingle-replacement",
      "fascia-soffit-repair-replacement",
      "moss-removal-roof-gutter",
    ],
    relatedAreaSlugs: ["burnaby", "coquitlam", "port-coquitlam"],
    solutionCategories: [
      {
        title: "Exhaust upgrades",
        body: "Add or correct ridge, box or other exhaust components as appropriate.",
      },
      {
        title: "Intake restoration",
        body: "Reopen or improve soffit intake paths where blocked.",
      },
      {
        title: "Balance review",
        body: "Aim for a coherent intake and exhaust strategy for the attic volume.",
      },
    ],
    repairVsReplace: {
      repair: "Upgrade ventilation when imbalance is documented.",
      replace: "Do not treat vents as a substitute for waterproofing repairs.",
    },
    scopeFactors: [
      "Attic volume",
      "Existing vent types",
      "Intake availability",
      "Roof covering type",
      "Access",
    ],
    path: "/services/roof-ventilation-upgrade",
    process: [
      "Review the attic and exterior ventilation components",
      "Evaluate intake and exhaust balance for the attic volume",
      "Written scope for vents, intake restoration and baffles",
      "Install and flash the new components",
      "Confirm airflow paths are unobstructed",
    ],
    publicPriceHint:
      "Ventilation projects are scoped by attic volume, existing vent types, intake availability and the roof covering involved. Request an assessment.",
    faqs: [
      {
        question: "What is a roof ventilation upgrade?",
        answer:
          "Roof ventilation upgrades improve intake and exhaust pathways so attic spaces can manage moisture and heat more effectively when the existing balance is inadequate.",
      },
      {
        question: "What are the signs of poor attic ventilation?",
        answer:
          "Condensation or frost on the underside of the sheathing in winter, musty attic smells, mould spotting, rusting nail tips and shingles aging unevenly across slopes. In coastal BC the dominant symptom is moisture rather than summer heat.",
      },
      {
        question: "Can adding more roof vents fix attic condensation?",
        answer:
          "Not by itself. Exhaust without matching soffit intake can pull air from the house instead of outside, sometimes making moisture worse. Bathroom fans venting into the attic and air leakage from the living space are frequently the real drivers, so the whole pathway is assessed.",
      },
      {
        question: "Ridge vents or box vents: what is the difference?",
        answer:
          "Ridge vents run continuously along the roof peak and exhaust evenly across the attic; box vents are discrete units placed near the ridge. Which suits a roof depends on ridge length, geometry and how intake is distributed.",
      },
      {
        question: "Will better ventilation make my shingles last longer?",
        answer:
          "Balanced ventilation reduces the attic heat and moisture loads that accelerate shingle aging and can contribute to deck problems. It is one factor among several - exposure, material quality and installation still matter - so it supports longevity rather than guaranteeing it.",
      },
    ],
  },
  {
    slug: "fascia-soffit-repair-replacement",
    title: "Fascia + Soffit Repair / Replacement",
    shortTitle: "Fascia & Soffit",
    eyebrow: "Eave components",
    category: "gutters-exterior",
    image: "fascia-soffit-repair-replacement",
    description:
      "Repair or replacement of rotten or damaged fascia, soffits and eave components that support gutters and edge detailing.",
    definition:
      "Fascia and soffit repair or replacement restores the eave assemblies that finish the roof edge, support gutters and contribute to intake ventilation.",
    metaTitle: "Fascia & Soffit Repair Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Fascia and soffit repair or replacement for Metro Vancouver homes. Restore eave structure, appearance and related intake pathways.",
    forWhom:
      "Owners with soft fascia boards, failing soffits, gutter detachment or pest access at eaves.",
    warningSigns: [
      "Soft or rotting fascia boards",
      "Sagging soffit panels",
      "Gutter brackets pulling free",
      "Paint failure with substrate decay",
      "Openings used by pests",
    ],
    evaluates: [
      "Linear extent of damage",
      "Material choice",
      "Ventilation needs at soffits",
      "Gutter reattachment plan",
      "Paint or finish scope",
    ],
    causes: [
      "Gutter overflow wetting wood",
      "Failed edge metal",
      "Original material end of life",
      "Ventilation moisture",
      "Impact or remodel damage",
    ],
    methodology: [
      "Probe and map damaged lengths",
      "Remove failed components",
      "Install new fascia/soffit materials",
      "Restore ventilation openings as designed",
      "Coordinate gutter reinstallation",
    ],
    materials: [
      "Fascia boards or cladding systems",
      "Soffit panels",
      "Fasteners and trim",
      "Paint or finish systems when included",
    ],
    bcClimate:
      "Chronic gutter overflow in rainy months is a common driver of fascia decay in the Lower Mainland.",
    whenAppropriate:
      "When eave components are damaged but the roof field may still be serviceable.",
    whenOtherService:
      "Combine with gutter or roof-edge work when those systems caused or will re-cause the damage.",
    relatedServiceSlugs: [
      "gutter-replacement",
      "roof-ventilation-upgrade",
      "asphalt-shingle-replacement",
      "gutter-cleaning-downspout-flush",
    ],
    relatedAreaSlugs: ["delta", "richmond", "white-rock"],
    solutionCategories: [
      {
        title: "Fascia restoration",
        body: "Replace decayed boards that support gutters and edge metal.",
      },
      {
        title: "Soffit repair",
        body: "Restore eave underside panels and intake openings.",
      },
      {
        title: "Eave coordination",
        body: "Sequence gutters and edge details with the new components.",
      },
    ],
    repairVsReplace: {
      repair: "Local repair when damage is limited.",
      replace: "Longer run replacement when decay is continuous along eaves.",
    },
    scopeFactors: [
      "Linear footage",
      "Material selection",
      "Height/access",
      "Finish requirements",
      "Gutter interaction",
    ],
    path: "/services/fascia-soffit-repair-replacement",
    process: [
      "Probe and map the damaged eave lengths",
      "Plan gutter detachment and reinstallation",
      "Written scope with material and finish choices",
      "Remove decayed components and install new material",
      "Restore intake openings and re-hang gutters",
    ],
    publicPriceHint:
      "Eave work follows linear footage, height and access, material and finish selection and how the gutters interact with the repair. Request an assessment.",
    faqs: [
      {
        question: "What is fascia + soffit repair / replacement?",
        answer:
          "Fascia and soffit repair or replacement restores the eave assemblies that finish the roof edge, support gutters and contribute to intake ventilation.",
      },
      {
        question: "What causes fascia boards to rot?",
        answer:
          "The usual local sequence is gutter overflow or a failed drip edge repeatedly wetting the board through the rainy season. Once the paint film fails, the wood stays damp and decay accelerates. Fixing the water source matters as much as replacing the board.",
      },
      {
        question: "Can fascia be replaced without removing the gutters?",
        answer:
          "Rarely well. Gutters are fastened through the fascia, so proper replacement means detaching and re-hanging them - which is also the opportunity to correct pitch and fasten into sound new material.",
      },
      {
        question: "Are my soffits part of the attic ventilation system?",
        answer:
          "In most homes, yes - soffit panels carry the intake openings that feed attic airflow. Replacement must preserve or restore that intake; blocking it during a cosmetic upgrade quietly creates attic moisture problems.",
      },
      {
        question: "Wood, aluminum or composite fascia: which should I choose?",
        answer:
          "Wood is traditional and repairable but needs finish maintenance. Aluminum cladding over wood resists weather but can trap moisture if detailed poorly. Composites resist rot with less upkeep. Exposure, budget and the home's architecture guide the choice.",
      },
    ],
  },
  {
    slug: "cedar-shake-repair-restoration",
    title: "Cedar Shake Repair / Restoration",
    shortTitle: "Cedar Repair",
    eyebrow: "Premium wood roofs",
    category: "emergency-repairs",
    image: "cedar-shake-repair-restoration",
    description:
      "Replace damaged cedar shakes and clean or restore localized areas on premium wood roofs.",
    definition:
      "Cedar shake repair restores discrete failed shakes and related details when the broader cedar roof remains worth maintaining.",
    metaTitle: "Cedar Shake Repair Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Cedar shake repair and localized restoration for Metro Vancouver wood roofs. Preserve serviceable cedar with targeted shake replacement.",
    forWhom: "Owners of cedar roofs with split, missing or cupped shakes in limited areas.",
    warningSigns: [
      "Individual shakes split or missing",
      "Localized cupping",
      "Moss holding moisture against wood",
      "Fastener stains with discrete failures",
      "Debris dams on low-slope cedar sections",
    ],
    evaluates: [
      "Matching shake availability",
      "Remaining field condition",
      "Cleaning needs",
      "Flashing interfaces",
      "Whether systemic replacement is nearer-term",
    ],
    causes: [
      "UV and wetting cycles",
      "Biological growth",
      "Impact",
      "Improper prior repairs",
      "Aging of premium wood roofs that still merit maintenance",
    ],
    methodology: [
      "Source matching shakes",
      "Replace failed units carefully",
      "Address debris and growth holding moisture",
      "Review flashings in the work zone",
      "Document remaining life indicators",
    ],
    materials: [
      "Cedar shakes of appropriate grade",
      "Underlayment patches if required",
      "Compatible fasteners",
      "Cleaning methods suitable for wood roofs",
    ],
    bcClimate:
      "BC’s wet climate is demanding on wood roofs; maintenance can extend useful life when the field is still fundamentally sound.",
    whenAppropriate:
      "When failures are localized and the broader cedar field remains maintainable.",
    whenOtherService:
      "System-wide cupping, leakage and underlayment failure point to full cedar replacement.",
    relatedServiceSlugs: [
      "cedar-shake-roof-replacement",
      "moss-removal-roof-gutter",
      "roof-health-check-maintenance",
      "flashing-roof-penetration-repair",
    ],
    relatedAreaSlugs: ["west-vancouver", "north-vancouver", "vancouver"],
    solutionCategories: [
      {
        title: "Selective shake replacement",
        body: "Restore failed units without discarding a maintainable field.",
      },
      {
        title: "Localized cleaning",
        body: "Reduce moisture-holding debris and growth around the repair zone.",
      },
      {
        title: "Condition triage",
        body: "Separate maintainable cedar from roofs needing full replacement discussion.",
      },
    ],
    repairVsReplace: {
      repair: "Repair when the field remains worth maintaining.",
      replace: "Replace when wood roof wear is systemic.",
    },
    scopeFactors: [
      "Area of repair",
      "Shake match",
      "Access",
      "Cleaning scope",
      "Adjacent flashing needs",
    ],
    path: "/services/cedar-shake-repair-restoration",
    process: [
      "Assess the field, growth and failure locations",
      "Source matching shakes of an appropriate grade",
      "Written scope for the repair and cleaning zones",
      "Replace failed shakes and address moisture-holding debris",
      "Document remaining-life indicators for planning",
    ],
    publicPriceHint:
      "Cedar repair reflects the repair area, shake matching, access, cleaning scope and adjacent flashing needs. Request an assessment.",
    faqs: [
      {
        question: "What is cedar shake repair / restoration?",
        answer:
          "Cedar shake repair restores discrete failed shakes and related details when the broader cedar roof remains worth maintaining.",
      },
      {
        question: "Is my cedar roof worth repairing, or should I replace it?",
        answer:
          "The deciding factors are the condition of the overall field and the underlayment beneath it, not the worst few shakes. Localized splits on an otherwise sound, well-draining field favour repair; widespread cupping, soft shakes and recurring leaks across planes favour replacement planning.",
      },
      {
        question: "Why are some of my shakes cupping and splitting?",
        answer:
          "Cedar moves with repeated wetting and drying cycles. Sun-exposed slopes dry aggressively and split; shaded, damp slopes stay wet and cup or grow moss. Age, shake grade and original fastening influence how early this appears.",
      },
      {
        question: "Can cedar be treated to make it last longer?",
        answer:
          "Keeping cedar clear of moss and debris is the highest-value maintenance in this climate, because trapped moisture drives decay. Surface treatments exist and are discussed case by case; they supplement good drainage and drying rather than replacing them.",
      },
      {
        question: "Will repaired areas match the existing cedar colour?",
        answer:
          "New cedar starts golden and weathers toward grey over seasons of exposure, so repairs are visible at first and blend gradually. Where appearance is critical, repair placement and expectations are discussed before work starts.",
      },
    ],
  },
  {
    slug: "flat-low-slope-roof-repair",
    title: "Flat / Low-Slope Roof Repair",
    shortTitle: "Flat Roof Repair",
    eyebrow: "Low-slope membranes",
    category: "emergency-repairs",
    image: "flat-low-slope-roof-repair",
    description:
      "SBS, torch-on, TPO/EPDM patching, flashing and penetration repairs on flat and low-slope roofs.",
    definition:
      "Flat and low-slope roof repair addresses membrane seams, flashings, penetrations and localized membrane failures without necessarily replacing the entire low-slope assembly.",
    metaTitle: "Flat Roof Repair Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Flat and low-slope roof repair for SBS, TPO, EPDM and related membranes in Metro Vancouver. Patch seams, flashings and penetrations when viable.",
    forWhom:
      "Owners with ponding seepage, failed seams, curb leaks or open membrane areas on low-slope roofs.",
    warningSigns: [
      "Seam openings",
      "Puncture marks",
      "Failed pitch pans or penetration pockets",
      "Blisters with active seepage",
      "Drains or scuppers not controlling water",
    ],
    evaluates: [
      "Membrane type",
      "Patch compatibility",
      "Drainage patterns",
      "Penetration condition",
      "Whether insulation is wet beyond local repair",
    ],
    causes: [
      "Membrane aging",
      "Foot traffic damage",
      "Ponding stress",
      "Flashing movement at parapets",
      "Incompatible prior patches",
    ],
    methodology: [
      "Identify membrane system",
      "Define compatible patch method",
      "Repair flashings and penetrations",
      "Improve local drainage details where practical",
      "Recommend replacement if the system is exhausted",
    ],
    materials: [
      "Compatible membrane patches",
      "Flashing metals and accessories",
      "Penetration pockets or prefabricated details",
      "Adhesives or heat processes as system-appropriate",
    ],
    bcClimate:
      "Long wet seasons and ponding risk make seam and drain detailing critical on Lower Mainland low-slope roofs.",
    whenAppropriate:
      "When failures are localized and the membrane field can still accept durable patches.",
    whenOtherService:
      "Saturated insulation or system-wide seam failure usually means replacement planning.",
    relatedServiceSlugs: [
      "flat-roof-replacement-sbs-tpo-pvc",
      "flashing-roof-penetration-repair",
      "emergency-roof-leak-repair",
      "roof-health-check-maintenance",
    ],
    relatedAreaSlugs: ["vancouver", "richmond", "burnaby"],
    solutionCategories: [
      {
        title: "Membrane patching",
        body: "Compatible repairs to seams, punctures and localized field damage.",
      },
      {
        title: "Penetration and curb detailing",
        body: "Restore waterproofing at rooftop openings.",
      },
      {
        title: "Drainage-focused corrections",
        body: "Address details that hold water against weak points.",
      },
    ],
    repairVsReplace: {
      repair: "Repair when the membrane remains broadly viable.",
      replace: "Replace when the system is saturated or repeatedly failing.",
    },
    scopeFactors: [
      "Membrane type",
      "Repair area",
      "Access",
      "Penetration count",
      "Drainage conditions",
    ],
    path: "/services/flat-low-slope-roof-repair",
    process: [
      "Identify the membrane system and failure points",
      "Moisture and drainage review around the damage",
      "Written scope for compatible patching and details",
      "Execute repairs with system-appropriate methods",
      "Review drainage behaviour and document the work",
    ],
    publicPriceHint:
      "Flat-roof repair pricing depends on membrane type, repair area, penetration count, drainage conditions and access. Request an assessment.",
    faqs: [
      {
        question: "What is flat / low-slope roof repair?",
        answer:
          "Flat and low-slope roof repair addresses membrane seams, flashings, penetrations and localized membrane failures without necessarily replacing the entire low-slope assembly.",
      },
      {
        question: "Why does the membrane type matter for a flat roof patch?",
        answer:
          "Each membrane bonds differently: SBS is torch- or adhesive-applied modified bitumen, TPO is heat-welded, and EPDM relies on adhesives and tapes. A patch of the wrong material or method will not bond durably: misidentified patches are a common cause of repeat leaks.",
      },
      {
        question: "Is ponding water on my flat roof a problem?",
        answer:
          "Standing water that persists well after rain stresses seams, accelerates membrane aging and magnifies any small defect. Some causes are repairable - blocked drains, localized low spots - while structural slope problems point toward a replacement-level discussion.",
      },
      {
        question: "Can a flat roof be repaired in winter?",
        answer:
          "Often yes, with method adjustments: some materials and adhesives have temperature limits, and surfaces must be dry. Emergency stabilization is nearly always possible; certain permanent details may wait for a suitable weather window.",
      },
      {
        question: "How do I know if the insulation under the membrane is wet?",
        answer:
          "Symptoms include persistent interior dampness, membrane blistering and soft or spongy areas underfoot; confirmation may need moisture scanning or test cuts. Wet insulation matters because patching over it traps moisture and continues the deterioration.",
      },
    ],
  },
  {
    slug: "metal-roof-repair-maintenance",
    title: "Metal Roof Repair / Maintenance",
    shortTitle: "Metal Repair",
    eyebrow: "Metal systems",
    category: "emergency-repairs",
    image: "metal-roof-repair-maintenance",
    description:
      "Fastener, flashing, sealant, panel and penetration repairs for metal roofing systems.",
    definition:
      "Metal roof repair and maintenance corrects fasteners, flashings, sealants, panel damage and penetrations on existing metal roofs while preserving serviceable panels.",
    metaTitle: "Metal Roof Repair Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Metal roof repair and maintenance for Metro Vancouver homes. Fasteners, flashings, closures and penetration corrections on existing metal systems.",
    forWhom:
      "Owners with loose fasteners, failed closures, penetration leaks or discrete panel damage on metal roofs.",
    warningSigns: [
      "Backed-out fasteners on exposed systems",
      "Failed foam closures",
      "Penetration collar leaks",
      "Sealant cracks at details",
      "Cut-edge corrosion starting at flashings",
    ],
    evaluates: [
      "Panel profile",
      "Coating condition",
      "Fastener type",
      "Closure and flashing details",
      "Whether damage is systemic",
    ],
    causes: [
      "Thermal cycling",
      "Incorrect fastener type",
      "Sealant aging",
      "Impact dents",
      "Coastal exposure effects",
    ],
    methodology: [
      "Identify system type",
      "Correct fasteners and closures as appropriate",
      "Repair flashings and penetrations",
      "Address discrete panel damage",
      "Avoid cosmetic sealant-only fixes where metal detailing is required",
    ],
    materials: [
      "Compatible fasteners and washers",
      "Closure strips",
      "Flashing metals",
      "Touch-up systems only when appropriate",
    ],
    bcClimate:
      "Coastal exposure and thermal movement keep fasteners and flashings as recurring maintenance points on metal roofs.",
    whenAppropriate: "When panels remain sound and issues are component-level.",
    whenOtherService:
      "System-wide coating failure or obsolete profiles may lead to replacement discussion.",
    relatedServiceSlugs: [
      "metal-roof-replacement",
      "flashing-roof-penetration-repair",
      "roof-health-check-maintenance",
      "skylight-leak-flashing-repair",
    ],
    relatedAreaSlugs: ["west-vancouver", "white-rock", "vancouver"],
    solutionCategories: [
      {
        title: "Fastener and closure maintenance",
        body: "Restore weathertight mechanical details.",
      },
      {
        title: "Flashing and penetration repair",
        body: "Correct interfaces that admit water.",
      },
      {
        title: "Panel-level fixes",
        body: "Address discrete damage without discarding a sound field.",
      },
    ],
    repairVsReplace: {
      repair: "Maintain and repair when the metal field is serviceable.",
      replace: "Replace when the system is no longer economically or technically maintainable.",
    },
    scopeFactors: [
      "System type",
      "Access",
      "Number of details",
      "Coating condition",
      "Penetration density",
    ],
    path: "/services/metal-roof-repair-maintenance",
    process: [
      "Identify the panel system and fastening type",
      "Inspect fasteners, closures, flashings and penetrations",
      "Written scope separating maintenance from repair",
      "Correct details with compatible components",
      "Review the roof as a working system",
    ],
    publicPriceHint:
      "Metal roof work is scoped by system type, the number of details needing correction, coating condition and access. Request an assessment.",
    faqs: [
      {
        question: "What is metal roof repair / maintenance?",
        answer:
          "Metal roof repair and maintenance corrects fasteners, flashings, sealants, panel damage and penetrations on existing metal roofs while preserving serviceable panels.",
      },
      {
        question: "Why are the screws backing out of my metal roof?",
        answer:
          "Exposed-fastener systems rely on gasketed screws that move with thermal cycling; over the years they can loosen and the gaskets harden. Correcting them - sometimes with oversized replacements - is normal maintenance for these systems, not a sign the roof has failed.",
      },
      {
        question: "My metal roof leaks at a vent pipe: is that common?",
        answer:
          "Penetrations are the most common leak points on metal roofs. Boots and collars age faster than panels and must accommodate panel movement. Replacing the boot with a compatible detail usually resolves it without touching the field.",
      },
      {
        question: "Can a dented metal panel be repaired?",
        answer:
          "Cosmetic dents that do not compromise the coating or seams can often stay. Damage that breaks the coating, opens a seam or holds water justifies panel-level repair or replacement of the affected sheet, which is practical on most profiles.",
      },
      {
        question: "How is standing-seam maintenance different from screw-down metal?",
        answer:
          "Standing seam conceals its fastening on clips, so routine gasket maintenance largely disappears; attention shifts to flashings, penetrations and sealant joints at terminations. Screw-down systems need periodic fastener checks as a matter of course.",
      },
    ],
  },
  {
    slug: "cedar-shake-roof-replacement",
    title: "Cedar Shake Roof Replacement",
    shortTitle: "Cedar Replacement",
    eyebrow: "Premium wood reroof",
    category: "replacement",
    image: "cedar-shake-roof-replacement",
    description:
      "Premium complete cedar shake reroof for homes where wood roofing is the intentional architectural choice.",
    definition:
      "Cedar shake roof replacement is a full reroof using cedar shakes or specified wood roofing components, including substrate preparation and correct detailing for a premium wood system.",
    metaTitle: "Cedar Shake Roof Replacement Vancouver BC | Revolin Roofing",
    metaDescription:
      "Complete cedar shake roof replacement for Metro Vancouver homes. Premium wood reroof with substrate preparation and proper detailing.",
    forWhom:
      "Owners committing to renew wood roof character when system-wide cedar wear makes repair no longer coherent.",
    warningSigns: [
      "System-wide cupping and splitting",
      "Repeated leaks across planes",
      "Underlayment failure under cedar",
      "Resale or planning concerns about remaining life",
      "Architectural intent to retain wood roofing",
    ],
    evaluates: [
      "Shake grade and profile",
      "Underlayment strategy",
      "Ventilation and drying",
      "Fire and project-context considerations",
      "Geometry complexity",
    ],
    causes: [
      "Full service-life completion",
      "Chronic moisture",
      "Storm losses across many planes",
      "Substrate issues discovered over time",
    ],
    methodology: [
      "Assess substrate and assembly needs",
      "Specify shake system and underlayment approach",
      "Protect the property through tear-off",
      "Install field and details as a complete wood roof assembly",
      "Review maintenance expectations for cedar",
    ],
    materials: [
      "Cedar shakes of specified grade",
      "Underlayment systems",
      "Flashings compatible with wood roofs",
      "Fasteners and ridge details",
    ],
    bcClimate:
      "Premium wood roofs in BC require honest maintenance expectations; replacement resets the assembly when repair no longer keeps up with systemic wear.",
    whenAppropriate:
      "When the homeowner wants a complete cedar system and the existing wood roof is at end of coherent service.",
    whenOtherService: "Localized failures on a still-strong field may still suit cedar repair.",
    relatedServiceSlugs: [
      "cedar-shake-repair-restoration",
      "roof-ventilation-upgrade",
      "flashing-roof-penetration-repair",
      "fascia-soffit-repair-replacement",
    ],
    relatedAreaSlugs: ["west-vancouver", "north-vancouver", "vancouver"],
    solutionCategories: [
      {
        title: "Complete cedar field installation",
        body: "New shakes installed as a coherent roof system.",
      },
      {
        title: "Substrate and underlayment preparation",
        body: "Address deck and water-shedding layers beneath the wood.",
      },
      {
        title: "Detailing for wood roofs",
        body: "Flashings and edges detailed for cedar performance.",
      },
    ],
    repairVsReplace: {
      repair: "Repair if only localized.",
      replace: "Full cedar reroof when wear is systemic and wood is still the chosen system.",
    },
    scopeFactors: [
      "Roof area",
      "Shake specification",
      "Substrate repairs",
      "Access",
      "Project complexity",
    ],
    path: "/services/cedar-shake-roof-replacement",
    process: [
      "Assess the existing wood roof and substrate",
      "Specify shake grade, underlayment and detailing",
      "Written scope and property-protection plan",
      "Tear-off, substrate preparation and installation",
      "Completion review and maintenance guidance",
    ],
    publicPriceHint:
      "Cedar reroof pricing reflects roof area and geometry, shake specification, substrate repairs and access. Request an assessment for a written scope.",
    faqs: [
      {
        question: "What is cedar shake roof replacement?",
        answer:
          "Cedar shake roof replacement is a full reroof using cedar shakes or specified wood roofing components, including substrate preparation and correct detailing for a premium wood system.",
      },
      {
        question: "How long does a cedar shake roof last in BC?",
        answer:
          "Service life varies widely with shake grade, exposure, detailing and above all maintenance, so a single number would be misleading. Well-built and well-maintained cedar roofs in this region commonly serve for decades; neglected ones fail much earlier. Realistic expectations for your slopes and shading are part of the assessment.",
      },
      {
        question: "What is the difference between cedar shakes and cedar shingles?",
        answer:
          "Shakes are split and thicker, with the textured face most people picture on premium wood roofs; shingles are sawn, thinner and more uniform. The choice affects appearance, detailing and cost, and it is settled during specification.",
      },
      {
        question: "Does a cedar roof need special underlayment?",
        answer:
          "Wood roofs need an underlayment strategy that sheds water while letting the cedar dry, because cedar performs poorly when it cannot breathe. Interlayment between courses and appropriate eave protection are typical elements of a correctly built assembly.",
      },
      {
        question: "Is cedar still a sensible roof choice in Metro Vancouver?",
        answer:
          "For the right home it remains a deliberate premium choice: architecture, neighbourhood character and the owner's commitment to maintenance all matter. The wet climate demands honest upkeep expectations; owners who prefer minimal maintenance are usually better served by another premium system.",
      },
    ],
  },
  {
    slug: "metal-roof-replacement",
    title: "Metal Roof Replacement",
    shortTitle: "Metal Replacement",
    eyebrow: "Premium metal reroof",
    category: "replacement",
    image: "metal-roof-replacement",
    description:
      "Standing-seam or other premium metal reroof for suitable steep-slope architecture.",
    definition:
      "Metal roof replacement installs a new metal roofing system - often standing seam - after assessment of geometry, substrate, underlayment and architectural intent.",
    metaTitle: "Metal Roof Replacement Metro Vancouver | Revolin Roofing",
    metaDescription:
      "Standing-seam and premium metal roof replacement for Metro Vancouver homes. Geometry-led system selection and complete flashing integration.",
    forWhom:
      "Owners choosing a premium metal system for suitable geometry, architecture and long-horizon planning.",
    warningSigns: [
      "Existing covering at end of life with metal selected as the new system",
      "Architecture suited to standing seam",
      "Desire for a premium covering profile",
      "Complex geometry better expressed in metal panels",
    ],
    evaluates: [
      "Profile selection",
      "Underlayment and substrate",
      "Condensation control approach",
      "Flashings and penetrations",
      "Acoustic and design expectations",
    ],
    causes: [
      "Planned system upgrade",
      "End-of-life existing roof",
      "Design renovation",
      "Performance goals aligned with metal assemblies",
    ],
    methodology: [
      "Confirm geometry suitability",
      "Specify panel system and underlayment",
      "Prepare substrate",
      "Install panels and flashings as a complete system",
      "Detail ridges, edges and penetrations intentionally",
    ],
    materials: [
      "Standing-seam or specified metal panels",
      "Underlayment systems",
      "Clips/fasteners as required by system",
      "Flashings and closures",
    ],
    bcClimate:
      "West Coast architecture often pairs well with standing-seam metal, but underlayment, ventilation and detailing still govern real-world performance in wet weather.",
    whenAppropriate:
      "When metal is the selected system and the project scope is a complete reroof.",
    whenOtherService:
      "Component repair may still serve if an existing metal roof is otherwise sound.",
    relatedServiceSlugs: [
      "metal-roof-repair-maintenance",
      "roof-ventilation-upgrade",
      "asphalt-shingle-replacement",
      "fascia-soffit-repair-replacement",
    ],
    relatedAreaSlugs: ["west-vancouver", "vancouver", "white-rock"],
    solutionCategories: [
      {
        title: "Standing-seam systems",
        body: "Premium panel aesthetics with concealed fastening where specified.",
      },
      {
        title: "Underlayment and substrate prep",
        body: "Build the water-shedding foundation beneath metal.",
      },
      {
        title: "Architectural detailing",
        body: "Edges, valleys and walls detailed for both performance and appearance.",
      },
    ],
    repairVsReplace: {
      repair: "Repair existing metal when panels remain sound.",
      replace:
        "Replace when a new metal system is the project goal or the old covering is finished.",
    },
    scopeFactors: [
      "Roof area and geometry",
      "Panel profile",
      "Underlayment choice",
      "Access",
      "Penetration complexity",
    ],
    path: "/services/metal-roof-replacement",
    process: [
      "Confirm geometry and system suitability",
      "Specify panel profile, underlayment and details",
      "Written scope with design decisions documented",
      "Substrate preparation and panel installation",
      "Detailing review and completion walkthrough",
    ],
    publicPriceHint:
      "Metal reroof pricing follows roof geometry, panel profile, underlayment strategy and penetration complexity. Request an assessment for a written scope.",
    faqs: [
      {
        question: "What is metal roof replacement?",
        answer:
          "Metal roof replacement installs a new metal roofing system - often standing seam - after assessment of geometry, substrate, underlayment and architectural intent.",
      },
      {
        question: "Is a metal roof noisy in the rain?",
        answer:
          "On a residential reroof over solid decking with underlayment and insulation below, rain noise is far less than most people expect - the loud association comes from panels over open framing. Acoustic expectations are still discussed during system selection.",
      },
      {
        question: "Standing seam versus exposed-fastener metal: what is the difference?",
        answer:
          "Standing seam conceals fastening on clips beneath raised seams, allowing panel movement and keeping gasketed screws out of the weather. Exposed-fastener panels cost less but carry long-term fastener maintenance. For premium residential work, standing seam is usually the recommendation.",
      },
      {
        question: "Can a metal roof be installed over my existing shingles?",
        answer:
          "Sometimes structurally possible, but tear-off is generally preferred so the deck can be inspected and the underlayment and flashing system built correctly. Trapping an old covering also complicates future diagnosis.",
      },
      {
        question: "Do metal roofs work on complex roof shapes?",
        answer:
          "Yes, but complexity is where metal projects are won or lost: hips, valleys, dormers and dead-end details demand skilled metalwork and increase cost. Geometry review is the first step in deciding whether metal suits the home.",
      },
    ],
  },
  {
    slug: "flat-roof-replacement-sbs-tpo-pvc",
    title: "Flat Roof Replacement: SBS / TPO / PVC",
    shortTitle: "Flat Roof Replacement",
    eyebrow: "Low-slope reroof",
    category: "replacement",
    image: "flat-roof-replacement-sbs-tpo-pvc",
    description:
      "Complete residential or commercial low-slope reroof using SBS, TPO, PVC or other specified membrane systems.",
    definition:
      "Flat roof replacement removes or recovers a low-slope roof assembly and installs a new membrane system selected for drainage, substrate and project requirements.",
    metaTitle: "SBS, TPO & PVC Flat Roof Replacement BC | Revolin Roofing",
    metaDescription:
      "Complete flat and low-slope roof replacement in Metro Vancouver using SBS, TPO, PVC or other specified membrane systems after assessment.",
    forWhom:
      "Owners with repeated membrane failures, wet insulation, obsolete systems or coordinated exterior renovations on low-slope roofs.",
    warningSigns: [
      "Multiple membrane failures",
      "Wet insulation",
      "Obsolete or unserviceable system",
      "Coordinated exterior renovation",
      "Chronic ponding with system-wide wear",
    ],
    evaluates: [
      "Membrane type options",
      "Insulation and substrate needs",
      "Drains and scuppers",
      "Parapets and penetrations",
      "Access and protection",
    ],
    causes: [
      "Membrane end of life",
      "Insulation saturation",
      "Drainage design limits",
      "UV and thermal aging",
      "Penetration density with accumulated detail failures",
    ],
    methodology: [
      "Assess assembly including wet components where investigation is warranted",
      "Select membrane strategy for the project",
      "Remove or recover as specified",
      "Install insulation and membrane layers in sequence",
      "Detail drains, parapets and penetrations as a system",
    ],
    materials: [
      "SBS, TPO, PVC or other specified membranes",
      "Insulation and cover boards as scoped",
      "Drain and flashing accessories",
      "Adhesives, fasteners or heat processes system-appropriate",
    ],
    bcClimate:
      "Low-slope roofs in rainy Metro Vancouver depend on drainage design and robust membrane detailing more than on marketing labels alone.",
    whenAppropriate:
      "When the low-slope assembly is exhausted or being renewed as part of a broader project.",
    whenOtherService:
      "Localized membrane repairs may still be viable on an otherwise sound system.",
    relatedServiceSlugs: [
      "flat-low-slope-roof-repair",
      "flashing-roof-penetration-repair",
      "roof-health-check-maintenance",
      "emergency-roof-leak-repair",
    ],
    relatedAreaSlugs: ["vancouver", "richmond", "burnaby"],
    solutionCategories: [
      {
        title: "Membrane system selection",
        body: "Choose SBS, TPO, PVC or another specified assembly for the project conditions.",
      },
      {
        title: "Assembly replacement",
        body: "Address membrane and related layers according to the approved scope.",
      },
      {
        title: "Drainage and detailing",
        body: "Drains, parapets and penetrations treated as core waterproofing work.",
      },
    ],
    repairVsReplace: {
      repair: "Repair if the membrane field is still viable.",
      replace: "Full low-slope replacement when the system is exhausted.",
    },
    scopeFactors: [
      "Area and geometry",
      "Membrane type",
      "Insulation needs",
      "Penetration density",
      "Access and disposal",
    ],
    path: "/services/flat-roof-replacement-sbs-tpo-pvc",
    process: [
      "Assembly assessment, including moisture investigation where warranted",
      "Membrane system selection and written scope",
      "Removal or recover of the existing assembly as specified",
      "Insulation and membrane installation in sequence",
      "Drain, parapet and penetration detailing review",
    ],
    publicPriceHint:
      "Low-slope replacement pricing follows area, membrane selection, insulation needs, drainage work and penetration density. Request an assessment.",
    faqs: [
      {
        question: "What is flat roof replacement with SBS, TPO or PVC?",
        answer:
          "Flat roof replacement removes or recovers a low-slope roof assembly and installs a new membrane system selected for drainage, substrate and project requirements.",
      },
      {
        question: "SBS, TPO or PVC: which flat roof membrane is best?",
        answer:
          "None is universally best. Two-ply SBS is the regional workhorse with robust redundancy; TPO and PVC are single-ply systems with heat-welded seams, lighter weight and reflective options, and PVC adds strong chemical resistance. Substrate, drainage, rooftop use and budget decide.",
      },
      {
        question: "Should we tear off or recover the existing flat roof?",
        answer:
          "Recovering can save cost and disposal when the existing assembly is dry and sound, but wet insulation or multiple existing layers make tear-off the honest path. Moisture investigation before the decision protects you from sealing problems in.",
      },
      {
        question: "How disruptive is a flat roof replacement?",
        answer:
          "Expect noise, odours during torch or adhesive work, staged material loading and protected access routes. Occupants can usually remain, and the written scope sets out sequencing, working hours and protection measures in advance.",
      },
      {
        question: "Can drainage be improved during flat roof replacement?",
        answer:
          "Replacement is the right moment to correct chronic ponding: tapered insulation, added or relocated drains and scupper work can be designed into the new assembly rather than fought afterward.",
      },
    ],
  },
];

/**
 * Complete catalogue, including protected published services and unpublished
 * expansion drafts. Consumers that render routes or navigation must use
 * `publishedServicePages` rather than this full inventory.
 */
export const servicePages: ServicePage[] = [
  ...protectedExistingServicePages.map(hydrateExistingService),
  ...newServiceDrafts.map(hydrateNewService),
];

/** The only service collection allowed to drive public routes and navigation. */
export const publishedServicePages: ServicePage[] = servicePages.filter(
  (service) => service.enabled,
);

export const serviceBySlug = Object.fromEntries(
  publishedServicePages.map((service) => [service.slug, service]),
) as Record<string, ServicePage>;

export function servicePath(slug: string): string {
  return serviceBySlug[slug]?.path ?? `/services/${slug}`;
}

export function servicesByCategory(category: ServiceCategoryId): ServicePage[] {
  return publishedServicePages.filter((service) => service.category === category);
}

export function relatedServices(service: ServicePage): ServicePage[] {
  return service.relatedServiceSlugs
    .map((slug) => serviceBySlug[slug])
    .filter((item): item is ServicePage => Boolean(item));
}

export function featuredServices(): ServicePage[] {
  return FEATURED_SERVICE_SLUGS.map((slug) => serviceBySlug[slug]).filter(
    (item): item is ServicePage => Boolean(item),
  );
}

/** Pricing disclaimer used whenever public pricing is enabled. */
export const PUBLIC_PRICING_DISCLAIMER =
  "Ranges shown are approximate starting points only. Actual pricing depends on roof size, slope, accessibility, existing condition, materials, disposal, flashing complexity, penetrations, structural conditions, required repairs, permit or code requirements and project location. Ranges are not guaranteed quotes.";

export function publicPricingEnabled(): boolean {
  return Boolean(business.showPublicPricing);
}
